import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { selfHelpApi, getMediaUrl } from '../services/api';

const MOOD_OPTIONS = [
  {
    id: 'anxious',
    emoji: '🌊',
    label: 'Anxious or Overwhelmed',
    color: '#7c3aed',
    bg: '#f5f3ff',
    border: '#ddd6fe',
    suggestion: 'Try a 60-second 4-7-8 Calming Breath or listen to an Anxiety Relief grounding audio.',
    recommendedTool: 1,
    recommendedBreathing: '478',
    keywords: ['anxiety', 'calm', 'grounding', 'stress', 'panic', 'breath'],
  },
  {
    id: 'sleep',
    emoji: '🌙',
    label: 'Trouble Sleeping',
    color: '#4338ca',
    bg: '#eef2ff',
    border: '#c7d2fe',
    suggestion: 'Wind down with Deep Sleep guided audio tracks or slow Coherent Breathing.',
    recommendedTool: 1,
    recommendedBreathing: 'coherent',
    keywords: ['sleep', 'night', 'relax', 'rest', 'insomnia', 'body scan'],
  },
  {
    id: 'low',
    emoji: '🌱',
    label: 'Low Mood or Drained',
    color: '#0369a1',
    bg: '#f0f9ff',
    border: '#bae6fd',
    suggestion: 'Explore uplifting therapist podcasts or gentle self-compassion worksheets.',
    recommendedTool: 2,
    recommendedBreathing: 'box',
    keywords: ['mood', 'depression', 'compassion', 'gratitude', 'mindfulness', 'positivity'],
  },
  {
    id: 'scattered',
    emoji: '🎯',
    label: 'Scattered & Need Focus',
    color: '#047857',
    bg: '#ecfdf5',
    border: '#a7f3d0',
    suggestion: 'Use Box Breathing (4-4-4-4) to reset your nervous system or try a structured CBT worksheet.',
    recommendedTool: 3,
    recommendedBreathing: 'box',
    keywords: ['focus', 'morning', 'cbt', 'thought', 'clarity', 'productivity'],
  },
];

const BREATHING_PATTERNS = {
  box: {
    name: 'Box Breathing (4-4-4-4)',
    subtitle: 'Used by clinicians & first responders for instant focus and emotional regulation',
    phases: [
      { label: 'Inhale Slowly', seconds: 4, scale: 1.38, color: '#7c3aed', hint: 'Breathe in through your nose...' },
      { label: 'Hold Gently', seconds: 4, scale: 1.38, color: '#6366f1', hint: 'Keep your shoulders relaxed...' },
      { label: 'Exhale Completely', seconds: 4, scale: 1.0, color: '#0ea5e9', hint: 'Release tension through your mouth...' },
      { label: 'Rest & Hold', seconds: 4, scale: 1.0, color: '#10b981', hint: 'Notice the stillness...' },
    ],
  },
  '478': {
    name: '4-7-8 Calming Breath',
    subtitle: 'Activates the parasympathetic nervous system to ease anxiety and prepare for sleep',
    phases: [
      { label: 'Inhale Quietly', seconds: 4, scale: 1.38, color: '#8b5cf6', hint: 'Draw air gently into your belly...' },
      { label: 'Deep Hold', seconds: 7, scale: 1.38, color: '#4f46e5', hint: 'Let oxygen settle through your body...' },
      { label: 'Long Whoosh Exhale', seconds: 8, scale: 1.0, color: '#06b6d4', hint: 'Exhale slowly and steadily...' },
    ],
  },
  coherent: {
    name: 'Coherent Resonance (5-5)',
    subtitle: 'Balances heart-rate variability and restores steady emotional equilibrium',
    phases: [
      { label: 'Smooth Inhale', seconds: 5, scale: 1.35, color: '#10b981', hint: 'Expand your ribcage softly...' },
      { label: 'Smooth Exhale', seconds: 5, scale: 1.0, color: '#0284c7', hint: 'Let go of every thought...' },
    ],
  },
};

const GROUNDING_STEPS = [
  { count: 5, emoji: '👀', title: 'Things You Can See', prompt: 'Look around and notice 5 distinct objects, colors, or textures in your environment.' },
  { count: 4, emoji: '✋', title: 'Things You Can Touch', prompt: 'Feel 4 physical sensations—your feet on the floor, fabric on your skin, or a cool surface.' },
  { count: 3, emoji: '👂', title: 'Things You Can Hear', prompt: 'Listen closely to 3 ambient sounds—distant traffic, a fan humming, or your own breath.' },
  { count: 2, emoji: '🌸', title: 'Things You Can Smell', prompt: 'Notice 2 scents in the air, or recall your two favorite calming aromas (like rain or coffee).' },
  { count: 1, emoji: '💜', title: 'Kind Thing About Yourself', prompt: 'Name 1 strength, effort, or gentle truth you appreciate about yourself today.' },
];

function SelfHelpPage() {
  const [selectedToolId, setSelectedToolId] = useState(1);
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [audioViewMode, setAudioViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMood, setSelectedMood] = useState(null);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  const [favorites, setFavorites] = useState(() => {
    try {
      const raw = window.localStorage.getItem('psychmitra_selfhelp_favs');
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  });

  const [activeWellnessModal, setActiveWellnessModal] = useState(null);
  const [previewResource, setPreviewResource] = useState(null);
  const [selectedPodcastTopic, setSelectedPodcastTopic] = useState('all');
  const [podcastPdfOnly, setPodcastPdfOnly] = useState(false);
  const [selectedWorksheetFilter, setSelectedWorksheetFilter] = useState('all');
  const [reflectionNote, setReflectionNote] = useState('');

  const [breathingMode, setBreathingMode] = useState('box');
  const [isBreathingActive, setIsBreathingActive] = useState(false);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(BREATHING_PATTERNS.box.phases[0].seconds);
  const [completedCycles, setCompletedCycles] = useState(0);

  const [groundingCompleted, setGroundingCompleted] = useState([]);

  const [nowPlaying, setNowPlaying] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [audioProgress, setAudioProgress] = useState({ currentTime: 0, duration: 0 });
  const stickyAudioRef = useRef(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resData, catData] = await Promise.all([
        selfHelpApi.getPublicResources(),
        selfHelpApi.getCategories({ section: 'audio' }),
      ]);
      setResources(resData.resources || []);
      setCategories(catData.categories || []);
    } catch (err) {
      console.error('Failed to fetch self-help dynamic data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setTimeout(() => {
      if (window.WOW) {
        try {
          new window.WOW().init();
        } catch (e) {
          // ignore
        }
      }
    }, 400);
  }, []);

  useEffect(() => {
    if (!isBreathingActive) return undefined;
    const pattern = BREATHING_PATTERNS[breathingMode];
    const timer = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev > 1) return prev - 1;
        setPhaseIndex((prevPhase) => {
          const nextPhase = (prevPhase + 1) % pattern.phases.length;
          if (nextPhase === 0) {
            setCompletedCycles((c) => c + 1);
          }
          setSecondsLeft(pattern.phases[nextPhase].seconds);
          return nextPhase;
        });
        return 0;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isBreathingActive, breathingMode]);

  const handleSelectBreathingMode = (modeKey) => {
    setBreathingMode(modeKey);
    setIsBreathingActive(false);
    setPhaseIndex(0);
    setSecondsLeft(BREATHING_PATTERNS[modeKey].phases[0].seconds);
  };

  const toggleFavorite = (id, e) => {
    if (e) e.stopPropagation();
    setFavorites((prev) => {
      const next = prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id];
      try {
        window.localStorage.setItem('psychmitra_selfhelp_favs', JSON.stringify(next));
      } catch (err) {
        // ignore
      }
      return next;
    });
  };

  const handlePlayTrack = (item) => {
    const src = item.audioUrl || item.mediaUrl;
    if (!src) return;
    const resolvedUrl = getMediaUrl(src);
    const id = item._id || item.id;

    if (nowPlaying && (nowPlaying._id || nowPlaying.id) === id) {
      if (stickyAudioRef.current) {
        if (isPlaying) {
          stickyAudioRef.current.pause();
          setIsPlaying(false);
        } else {
          stickyAudioRef.current.play().catch(() => { });
          setIsPlaying(true);
        }
      }
      return;
    }

    setNowPlaying({ ...item, resolvedUrl });
    setIsPlaying(true);
    setTimeout(() => {
      if (stickyAudioRef.current) {
        stickyAudioRef.current.playbackRate = playbackRate;
        stickyAudioRef.current.play().catch(() => { });
      }
    }, 80);
  };

  const handleCyclePlaybackRate = () => {
    const rates = [1, 1.25, 1.5, 0.85];
    const nextRate = rates[(rates.indexOf(playbackRate) + 1) % rates.length];
    setPlaybackRate(nextRate);
    if (stickyAudioRef.current) {
      stickyAudioRef.current.playbackRate = nextRate;
    }
  };

  const formatTime = (sec) => {
    if (!sec || Number.isNaN(sec)) return '0:00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const matchesFilter = useCallback(
    (r) => {
      const id = r._id || r.id;
      if (showFavoritesOnly && !favorites.includes(id)) return false;
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        (r.title || '').toLowerCase().includes(q) ||
        (r.category || '').toLowerCase().includes(q) ||
        (r.summary || '').toLowerCase().includes(q) ||
        (r.narrator || '').toLowerCase().includes(q)
      );
    },
    [showFavoritesOnly, favorites, searchQuery]
  );

  const audioResources = useMemo(
    () =>
      resources.filter(
        (r) =>
          (r.section === 'audio' || r.type === 'audio' || (!r.section && !r.pdfUrl && !r.worksheetFileUrl)) &&
          matchesFilter(r)
      ),
    [resources, matchesFilter]
  );

  const podcastResources = useMemo(
    () =>
      resources.filter(
        (r) =>
          (r.section === 'podcast' || r.type === 'guide' || (r.pdfUrl && r.section !== 'activity')) &&
          matchesFilter(r)
      ),
    [resources, matchesFilter]
  );

  const activityResources = useMemo(
    () =>
      resources.filter(
        (r) => (r.section === 'activity' || r.type === 'worksheet' || r.worksheetFileUrl) && matchesFilter(r)
      ),
    [resources, matchesFilter]
  );

  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim() && !showFavoritesOnly) return categories;
    const q = searchQuery.toLowerCase();
    return categories.filter((cat) => {
      const nameMatch = (cat.name || '').toLowerCase().includes(q);
      const hasMatchingAudio = audioResources.some(
        (a) => a.category?.trim().toLowerCase() === cat.name?.trim().toLowerCase()
      );
      return showFavoritesOnly ? hasMatchingAudio : nameMatch || hasMatchingAudio;
    });
  }, [categories, searchQuery, audioResources, showFavoritesOnly]);

  const categoryAudios = selectedCategory
    ? audioResources.filter(
      (a) => a.category?.trim().toLowerCase() === selectedCategory.name?.trim().toLowerCase()
    )
    : [];

  const getCategoryIcon = (name = '') => {
    const lower = name.toLowerCase();
    if (lower.includes('sleep') || lower.includes('night')) return '🌙';
    if (lower.includes('anxiet') || lower.includes('panic') || lower.includes('fear')) return '🌊';
    if (lower.includes('breath')) return '🌬️';
    if (lower.includes('morning') || lower.includes('energy')) return '☀️';
    if (lower.includes('focus') || lower.includes('study') || lower.includes('work')) return '🎯';
    if (lower.includes('stress') || lower.includes('relax')) return '🍃';
    if (lower.includes('meditat') || lower.includes('mindful')) return '🧘';
    if (lower.includes('self') || lower.includes('love') || lower.includes('confiden')) return '💜';
    if (lower.includes('grief') || lower.includes('heal')) return '🕊️';
    return '🎵';
  };

  const getCategoryVisual = (name = '', idx = 0) => {
    const lower = (name || '').toLowerCase();
    const localFallbacks = [
      '/assets/images/auth-soothing-wellness.jpg',
      '/assets/images/backgrounds/slider-2-1.jpg',
      '/assets/images/backgrounds/slider-1-1.jpg',
      '/assets/images/blog/blog-1-1.jpg',
      '/assets/images/blog/blog-1-2.jpg',
      '/assets/images/blog/blog-1-3.jpg',
      '/assets/images/blog/blog-1-4.jpg',
      '/assets/images/blog/blog-1-5.jpg',
    ];
    const fallback = localFallbacks[idx % localFallbacks.length];

    if (lower.includes('sleep') || lower.includes('night') || lower.includes('insomnia')) {
      return {
        image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=900&q=80',
        fallback,
        overlay: 'linear-gradient(180deg, rgba(15, 23, 42, 0.15) 0%, rgba(30, 27, 75, 0.86) 100%)',
        accent: '#6366f1',
        badgeBg: '#eef2ff',
        tagline: 'Drift into deep, restorative rest with soothing nightscapes & body scans.',
      };
    }
    if (lower.includes('anxiet') || lower.includes('panic') || lower.includes('overwhelm') || lower.includes('fear')) {
      return {
        image: 'https://images.unsplash.com/photo-1505118380757-91f5f5632de0?auto=format&fit=crop&w=900&q=80',
        fallback,
        overlay: 'linear-gradient(180deg, rgba(14, 116, 144, 0.12) 0%, rgba(15, 23, 42, 0.84) 100%)',
        accent: '#0284c7',
        badgeBg: '#f0f9ff',
        tagline: 'Gentle ocean-calm grounding to quiet racing thoughts and ease tension.',
      };
    }
    if (lower.includes('breath') || lower.includes('ground')) {
      return {
        image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=900&q=80',
        fallback,
        overlay: 'linear-gradient(180deg, rgba(5, 150, 105, 0.12) 0%, rgba(6, 78, 59, 0.85) 100%)',
        accent: '#059669',
        badgeBg: '#ecfdf5',
        tagline: 'Conscious breathwork & forest stillness to regulate your nervous system.',
      };
    }
    if (lower.includes('morning') || lower.includes('energy') || lower.includes('positiv') || lower.includes('gratitude')) {
      return {
        image: 'https://images.unsplash.com/photo-1470252649378-9c29740c9fa8?auto=format&fit=crop&w=900&q=80',
        fallback,
        overlay: 'linear-gradient(180deg, rgba(217, 119, 6, 0.1) 0%, rgba(69, 26, 3, 0.82) 100%)',
        accent: '#d97706',
        badgeBg: '#fffbeb',
        tagline: 'Warm sunrise affirmations and uplifting intention-setting for your day.',
      };
    }
    if (lower.includes('focus') || lower.includes('study') || lower.includes('work') || lower.includes('clarity')) {
      return {
        image: 'https://images.unsplash.com/photo-1518241353330-0f7941c2d9b5?auto=format&fit=crop&w=900&q=80',
        fallback,
        overlay: 'linear-gradient(180deg, rgba(15, 118, 110, 0.12) 0%, rgba(19, 78, 74, 0.85) 100%)',
        accent: '#0d9488',
        badgeBg: '#f0fdfa',
        tagline: 'Clear mental clutter and enter a calm, centered state of deep focus.',
      };
    }
    if (lower.includes('stress') || lower.includes('relax') || lower.includes('calm')) {
      return {
        image: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=900&q=80',
        fallback,
        overlay: 'linear-gradient(180deg, rgba(79, 70, 229, 0.12) 0%, rgba(30, 27, 75, 0.84) 100%)',
        accent: '#4f46e5',
        badgeBg: '#eef2ff',
        tagline: 'Release physical tightness and unwind with serene nature visualizations.',
      };
    }
    if (lower.includes('self') || lower.includes('love') || lower.includes('compassion') || lower.includes('confiden')) {
      return {
        image: 'https://images.unsplash.com/photo-1490750967868-88aa4486c946?auto=format&fit=crop&w=900&q=80',
        fallback,
        overlay: 'linear-gradient(180deg, rgba(190, 24, 93, 0.12) 0%, rgba(80, 7, 36, 0.82) 100%)',
        accent: '#db2777',
        badgeBg: '#fdf2f8',
        tagline: 'Nurture inner kindness, emotional resilience, and gentle self-acceptance.',
      };
    }

    const palette = [
      {
        image: 'https://images.unsplash.com/photo-1519834785169-98be25ec3f84?auto=format&fit=crop&w=900&q=80',
        overlay: 'linear-gradient(180deg, rgba(109, 40, 217, 0.14) 0%, rgba(30, 27, 75, 0.86) 100%)',
        accent: '#7c3aed',
        badgeBg: '#f5f3ff',
        tagline: 'Guided mindfulness and therapeutic audio sessions for inner balance.',
      },
      {
        image: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=900&q=80',
        overlay: 'linear-gradient(180deg, rgba(2, 132, 199, 0.14) 0%, rgba(15, 23, 42, 0.86) 100%)',
        accent: '#0284c7',
        badgeBg: '#f0f9ff',
        tagline: 'Soothing guided reflections to help you pause, breathe, and reconnect.',
      },
      {
        image: 'https://images.unsplash.com/photo-1501854140801-50d01698950b?auto=format&fit=crop&w=900&q=80',
        overlay: 'linear-gradient(180deg, rgba(5, 150, 105, 0.14) 0%, rgba(6, 78, 59, 0.86) 100%)',
        accent: '#059669',
        badgeBg: '#ecfdf5',
        tagline: 'Peaceful acoustic and voice-guided practices for everyday emotional care.',
      },
    ];
    const picked = palette[idx % palette.length];
    return { ...picked, fallback };
  };

  const getPodcastVisual = (item = {}, idx = 0) => {
    const lower = `${item.category || ''} ${item.title || ''}`.toLowerCase();
    const fallbacks = [
      '/assets/images/blog/blog-1-1.jpg',
      '/assets/images/blog/blog-1-2.jpg',
      '/assets/images/blog/blog-1-3.jpg',
      '/assets/images/blog/blog-1-4.jpg',
      '/assets/images/backgrounds/slider-1-1.jpg',
      '/assets/images/auth-soothing-wellness.jpg',
    ];
    const fallback = fallbacks[idx % fallbacks.length];

    const visuals = [
      {
        image: 'https://images.unsplash.com/photo-1590602847861-f357a9332bbc?auto=format&fit=crop&w=900&q=80',
        overlay: 'linear-gradient(180deg, rgba(12, 74, 110, 0.18) 0%, rgba(15, 23, 42, 0.88) 100%)',
        badge: '🎙️ Clinical Conversation',
      },
      {
        image: 'https://images.unsplash.com/photo-1478737270239-2f02b77fc618?auto=format&fit=crop&w=900&q=80',
        overlay: 'linear-gradient(180deg, rgba(3, 105, 161, 0.16) 0%, rgba(15, 23, 42, 0.88) 100%)',
        badge: '🎧 Therapist Insights',
      },
      {
        image: 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=900&q=80',
        overlay: 'linear-gradient(180deg, rgba(30, 58, 138, 0.18) 0%, rgba(15, 23, 42, 0.88) 100%)',
        badge: '🧠 Mind & Emotion Deep-Dive',
      },
      {
        image: 'https://images.unsplash.com/photo-1516280440614-37939bbacd81?auto=format&fit=crop&w=900&q=80',
        overlay: 'linear-gradient(180deg, rgba(14, 116, 144, 0.16) 0%, rgba(15, 23, 42, 0.88) 100%)',
        badge: '💬 Wellness Dialogue',
      },
    ];

    if (lower.includes('anxiet') || lower.includes('stress') || lower.includes('burnout')) {
      return { ...visuals[0], fallback };
    }
    if (lower.includes('relation') || lower.includes('family') || lower.includes('couple')) {
      return { ...visuals[1], fallback };
    }
    if (lower.includes('sleep') || lower.includes('mindful') || lower.includes('meditat')) {
      return { ...visuals[2], fallback };
    }
    return { ...visuals[idx % visuals.length], fallback };
  };

  const getWorksheetVisual = (item = {}, idx = 0) => {
    const lower = `${item.category || ''} ${item.title || ''}`.toLowerCase();
    const fallbacks = [
      '/assets/images/blog/blog-1-5.jpg',
      '/assets/images/blog/blog-1-6.jpg',
      '/assets/images/blog/blog-2-1.jpg',
      '/assets/images/blog/blog-2-2.jpg',
      '/assets/images/backgrounds/slider-2-2.jpg',
      '/assets/images/auth-soothing-wellness.jpg',
    ];
    const fallback = fallbacks[idx % fallbacks.length];

    const visuals = [
      {
        image: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&w=900&q=80',
        overlay: 'linear-gradient(180deg, rgba(6, 78, 59, 0.16) 0%, rgba(6, 78, 59, 0.86) 100%)',
        tag: '✍️ Guided Reflection',
      },
      {
        image: 'https://images.unsplash.com/photo-1517842645767-c639042777db?auto=format&fit=crop&w=900&q=80',
        overlay: 'linear-gradient(180deg, rgba(5, 150, 105, 0.15) 0%, rgba(15, 23, 42, 0.86) 100%)',
        tag: '🧠 CBT Exercise',
      },
      {
        image: 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?auto=format&fit=crop&w=900&q=80',
        overlay: 'linear-gradient(180deg, rgba(13, 148, 136, 0.15) 0%, rgba(19, 78, 74, 0.86) 100%)',
        tag: '🌱 Daily Habit Tracker',
      },
      {
        image: 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&w=900&q=80',
        overlay: 'linear-gradient(180deg, rgba(4, 120, 87, 0.16) 0%, rgba(15, 23, 42, 0.86) 100%)',
        tag: '📋 Clinical Worksheet',
      },
    ];

    if (lower.includes('cbt') || lower.includes('thought') || lower.includes('cognitive')) {
      return { ...visuals[1], fallback };
    }
    if (lower.includes('journal') || lower.includes('gratitude') || lower.includes('mood')) {
      return { ...visuals[0], fallback };
    }
    if (lower.includes('goal') || lower.includes('habit') || lower.includes('routine')) {
      return { ...visuals[2], fallback };
    }
    return { ...visuals[idx % visuals.length], fallback };
  };

  const tools = [
    {
      id: 1,
      name: 'Audio Resources',
      badge: 'Guided Audio Sanctuary',
      icon: '🎵',
      accent: '#7c3aed',
      accentLight: '#f5f3ff',
      accentBorder: '#ddd6fe',
      gradient: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
      coverImage: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=900&q=80',
      coverFallback: '/assets/images/auth-soothing-wellness.jpg',
      coverOverlay: 'linear-gradient(180deg, rgba(30, 27, 75, 0.18) 0%, rgba(76, 29, 149, 0.85) 100%)',
      description:
        'Listen to guided audio tracks organized category-wise for deep sleep, anxiety relief, morning focus, and grounding.',
      btnText: 'Explore Audio Sanctuary',
      count: `${categories.length || 14} Categories • ${audioResources.length} Tracks`,
    },
    {
      id: 2,
      name: 'Podcasts & Discussions',
      badge: 'Clinical Insights & PDFs',
      icon: '🎧',
      accent: '#0284c7',
      accentLight: '#f0f9ff',
      accentBorder: '#bae6fd',
      gradient: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
      coverImage: 'https://images.unsplash.com/photo-1590602847861-f357a9332bbc?auto=format&fit=crop&w=900&q=80',
      coverFallback: '/assets/images/blog/blog-1-1.jpg',
      coverOverlay: 'linear-gradient(180deg, rgba(12, 74, 110, 0.18) 0%, rgba(3, 105, 161, 0.86) 100%)',
      description:
        'Explore insightful therapist discussions and downloadable companion PDF guides, episode notes, and transcripts.',
      btnText: 'View Podcasts & PDFs',
      count: `${podcastResources.length} Episodes & Guides`,
    },
    {
      id: 3,
      name: 'Activities & Worksheets',
      badge: 'CBT & Daily Exercises',
      icon: '📝',
      accent: '#059669',
      accentLight: '#ecfdf5',
      accentBorder: '#a7f3d0',
      gradient: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
      coverImage: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&w=900&q=80',
      coverFallback: '/assets/images/blog/blog-1-5.jpg',
      coverOverlay: 'linear-gradient(180deg, rgba(6, 78, 59, 0.18) 0%, rgba(5, 150, 105, 0.86) 100%)',
      description:
        'Download actionable mental wellness worksheets, CBT thought records, and practical self-help exercises.',
      btnText: 'View Activities & Worksheets',
      count: `${activityResources.length} Printable Worksheets`,
    },
  ];

  const handleToolClick = (toolId) => {
    setSelectedToolId(toolId);
    setSelectedCategory(null);
    setTimeout(() => {
      const detailEl = document.getElementById('self-help-detail-section');
      if (detailEl) {
        detailEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const currentBreathingPattern = BREATHING_PATTERNS[breathingMode];
  const currentPhase = currentBreathingPattern.phases[phaseIndex] || currentBreathingPattern.phases[0];

  return (
    <>
      <style>{`
        .sh-tool-card {
          transition: all 0.32s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .sh-tool-card:hover {
          transform: translateY(-8px) !important;
        }
        .sh-resource-card {
          transition: all 0.25s ease;
        }
        .sh-resource-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 16px 32px rgba(30, 27, 75, 0.09) !important;
        }
        .sh-mood-chip {
          transition: all 0.2s ease;
        }
        .sh-mood-chip:hover {
          transform: translateY(-2px);
        }
        .sh-folder-row {
          transition: all 0.18s ease;
        }
        .sh-folder-row:hover {
          padding-left: 34px !important;
        }
        .sh-audio-card-cover {
          transition: transform 0.55s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .sh-resource-card:hover .sh-audio-card-cover {
          transform: scale(1.07);
        }
        @keyframes shEqPulse {
          0%, 100% { height: 6px; }
          50% { height: 18px; }
        }
        .sh-eq-bar {
          width: 3px;
          background: #a78bfa;
          border-radius: 999px;
          display: inline-block;
          animation: shEqPulse 0.85s ease-in-out infinite;
        }
      `}</style>

      {/* Main Toolkit Section */}
      <section
        className="self-help-section"
        style={{
          padding: '150px 0 120px 0',
          background: 'linear-gradient(180deg, #faf7fd 0%, #f3effe 45%, #f0f9ff 100%)',
          minHeight: '100vh',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div className="container">
          {/* Header Title */}
          <div className="row">
            <div className="col-lg-12">
              <div className="section-title text-center wow fadeInUp" style={{ marginBottom: '36px' }}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: '#ede9fe',
                    color: '#6d28d9',
                    padding: '6px 18px',
                    borderRadius: '999px',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    letterSpacing: '0.03em',
                    marginBottom: '14px',
                  }}
                >
                  ✨ Evidence-Based Wellness Library
                </span>
                <h2
                  className="section-title__title"
                  style={{
                    fontSize: 'clamp(2rem, 3.5vw, 2.75rem)',
                    fontWeight: 800,
                    color: '#1e1b4b',
                    marginBottom: '14px',
                    letterSpacing: '-0.02em',
                  }}
                >
                  Your Personal Self-Help Toolkit
                </h2>
                <p
                  style={{
                    color: '#475569',
                    fontSize: '1.08rem',
                    maxWidth: '680px',
                    margin: '0 auto',
                    lineHeight: 1.65,
                  }}
                >
                  Explore guided audio sessions, therapist podcasts with companion PDFs, printable CBT worksheets, and
                  interactive grounding exercises designed to support your everyday mental well-being.
                </p>
              </div>
            </div>
          </div>

          {/* INTERACTIVE MOOD CHECK-IN & INSTANT RELIEF BAR */}
          <div
            style={{
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(12px)',
              borderRadius: '24px',
              padding: '28px 32px',
              marginBottom: '40px',
              border: '1px solid rgba(124, 58, 237, 0.14)',
              boxShadow: '0 12px 36px rgba(109, 40, 217, 0.06)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '16px',
                marginBottom: '18px',
              }}
            >
              <div>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#1e1b4b', margin: 0 }}>
                  🌿 How are you feeling right now?
                </h3>
                <p style={{ fontSize: '13.5px', color: '#64748b', margin: '4px 0 0 0' }}>
                  Select your current state for tailored recommendations, or launch an instant 60-second exercise.
                </p>
              </div>

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => {
                    setActiveWellnessModal(activeWellnessModal === 'breathing' ? null : 'breathing');
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 18px',
                    borderRadius: '12px',
                    border: activeWellnessModal === 'breathing' ? 'none' : '1.5px solid #c4b5fd',
                    background:
                      activeWellnessModal === 'breathing'
                        ? 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)'
                        : '#f5f3ff',
                    color: activeWellnessModal === 'breathing' ? '#ffffff' : '#6d28d9',
                    fontWeight: 700,
                    fontSize: '13.5px',
                    cursor: 'pointer',
                    boxShadow:
                      activeWellnessModal === 'breathing' ? '0 6px 18px rgba(124, 58, 237, 0.28)' : 'none',
                  }}
                >
                  🌬️ {activeWellnessModal === 'breathing' ? 'Close Breathing Pacer' : 'Interactive Breathing Pacer'}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setActiveWellnessModal(activeWellnessModal === 'grounding' ? null : 'grounding');
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 18px',
                    borderRadius: '12px',
                    border: activeWellnessModal === 'grounding' ? 'none' : '1.5px solid #a7f3d0',
                    background:
                      activeWellnessModal === 'grounding'
                        ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                        : '#ecfdf5',
                    color: activeWellnessModal === 'grounding' ? '#ffffff' : '#047857',
                    fontWeight: 700,
                    fontSize: '13.5px',
                    cursor: 'pointer',
                    boxShadow:
                      activeWellnessModal === 'grounding' ? '0 6px 18px rgba(5, 150, 105, 0.25)' : 'none',
                  }}
                >
                  🧭 {activeWellnessModal === 'grounding' ? 'Close 5-4-3-2-1 Tool' : '5-4-3-2-1 Grounding Reset'}
                </button>
              </div>
            </div>

            {/* Mood Chips */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {MOOD_OPTIONS.map((mood) => {
                const isActive = selectedMood?.id === mood.id;
                return (
                  <button
                    key={mood.id}
                    type="button"
                    className="sh-mood-chip"
                    onClick={() => setSelectedMood(isActive ? null : mood)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '11px 18px',
                      borderRadius: '14px',
                      border: `2px solid ${isActive ? mood.color : mood.border}`,
                      background: isActive ? mood.color : mood.bg,
                      color: isActive ? '#ffffff' : '#1e293b',
                      fontWeight: 700,
                      fontSize: '13.5px',
                      cursor: 'pointer',
                      boxShadow: isActive ? `0 6px 16px ${mood.color}33` : 'none',
                    }}
                  >
                    <span style={{ fontSize: '18px' }}>{mood.emoji}</span>
                    <span>{mood.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Personalized Mood Recommendation Banner */}
            {selectedMood && (
              <div
                style={{
                  marginTop: '18px',
                  padding: '16px 20px',
                  borderRadius: '16px',
                  background: selectedMood.bg,
                  border: `1.5px solid ${selectedMood.border}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '12px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '26px' }}>{selectedMood.emoji}</span>
                  <div>
                    <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '14px' }}>
                      Recommended for "{selectedMood.label}"
                    </div>
                    <div style={{ fontSize: '13px', color: '#475569', marginTop: '2px' }}>
                      {selectedMood.suggestion}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => {
                      handleSelectBreathingMode(selectedMood.recommendedBreathing);
                      setActiveWellnessModal('breathing');
                    }}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '10px',
                      border: `1px solid ${selectedMood.color}`,
                      background: '#ffffff',
                      color: selectedMood.color,
                      fontWeight: 700,
                      fontSize: '12.5px',
                      cursor: 'pointer',
                    }}
                  >
                    🌬️ Try Guided Breath
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToolClick(selectedMood.recommendedTool)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '10px',
                      border: 'none',
                      background: selectedMood.color,
                      color: '#ffffff',
                      fontWeight: 700,
                      fontSize: '12.5px',
                      cursor: 'pointer',
                    }}
                  >
                    Open Recommended Resources →
                  </button>
                </div>
              </div>
            )}

            {/* INTERACTIVE BREATHING PACER DRAWER */}
            {activeWellnessModal === 'breathing' && (
              <div
                style={{
                  marginTop: '22px',
                  padding: '28px',
                  borderRadius: '20px',
                  background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
                  color: '#ffffff',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: '14px',
                    marginBottom: '20px',
                  }}
                >
                  <div>
                    <span
                      style={{
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        color: '#c4b5fd',
                        fontWeight: 700,
                      }}
                    >
                      Interactive Nervous System Reset
                    </span>
                    <h4 style={{ fontSize: '1.35rem', fontWeight: 800, color: '#ffffff', margin: '4px 0' }}>
                      {currentBreathingPattern.name}
                    </h4>
                    <p style={{ fontSize: '13px', color: '#cbd5e1', margin: 0 }}>
                      {currentBreathingPattern.subtitle}
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {Object.entries(BREATHING_PATTERNS).map(([key, pat]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => handleSelectBreathingMode(key)}
                        style={{
                          padding: '7px 13px',
                          borderRadius: '10px',
                          border: breathingMode === key ? '1.5px solid #a78bfa' : '1px solid rgba(255,255,255,0.2)',
                          background: breathingMode === key ? 'rgba(167, 139, 250, 0.25)' : 'rgba(255,255,255,0.06)',
                          color: '#ffffff',
                          fontSize: '12px',
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        {pat.name.split(' (')[0]}
                      </button>
                    ))}
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '28px 0 18px 0',
                  }}
                >
                  <div
                    style={{
                      width: '165px',
                      height: '165px',
                      borderRadius: '50%',
                      background: `radial-gradient(circle, ${currentPhase.color} 0%, rgba(99, 102, 241, 0.35) 75%)`,
                      boxShadow: `0 0 50px ${currentPhase.color}88`,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transform: `scale(${isBreathingActive ? currentPhase.scale : 1})`,
                      transition: `transform ${currentPhase.seconds}s ease-in-out, background 0.6s ease`,
                      border: '2px solid rgba(255,255,255,0.35)',
                      marginBottom: '26px',
                    }}
                  >
                    <div style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.03em' }}>
                      {isBreathingActive ? currentPhase.label : 'Ready'}
                    </div>
                    <div style={{ fontSize: '36px', fontWeight: 900, lineHeight: 1.1, marginTop: '4px' }}>
                      {isBreathingActive ? `${secondsLeft}s` : '🌬️'}
                    </div>
                  </div>

                  <p style={{ fontSize: '14px', color: '#e2e8f0', marginBottom: '18px', textAlign: 'center' }}>
                    {isBreathingActive
                      ? currentPhase.hint
                      : 'Sit comfortably, relax your shoulders, and press Start to follow the breathing orb.'}
                  </p>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <button
                      type="button"
                      onClick={() => setIsBreathingActive(!isBreathingActive)}
                      style={{
                        padding: '11px 28px',
                        borderRadius: '12px',
                        border: 'none',
                        background: isBreathingActive ? '#ef4444' : '#10b981',
                        color: '#ffffff',
                        fontWeight: 800,
                        fontSize: '14px',
                        cursor: 'pointer',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
                      }}
                    >
                      {isBreathingActive ? '⏸ Pause Exercise' : '▶ Start Breathing'}
                    </button>
                    <span style={{ fontSize: '13px', color: '#cbd5e1', fontWeight: 600 }}>
                      Completed Cycles: <strong>{completedCycles}</strong>
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* INTERACTIVE 5-4-3-2-1 SENSORY GROUNDING DRAWER */}
            {activeWellnessModal === 'grounding' && (
              <div
                style={{
                  marginTop: '22px',
                  padding: '26px',
                  borderRadius: '20px',
                  background: 'linear-gradient(135deg, #ecfdf5 0%, #f0f9ff 100%)',
                  border: '1.5px solid #a7f3d0',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '10px',
                    marginBottom: '16px',
                  }}
                >
                  <div>
                    <h4 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#065f46', margin: 0 }}>
                      🧭 5-4-3-2-1 Sensory Grounding Checklist
                    </h4>
                    <p style={{ fontSize: '13px', color: '#047857', margin: '4px 0 0 0' }}>
                      Tap each step as you complete it to anchor your mind back into the present moment.
                    </p>
                  </div>
                  {groundingCompleted.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setGroundingCompleted([])}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '8px',
                        border: '1px solid #059669',
                        background: '#ffffff',
                        color: '#059669',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      ↺ Reset Steps
                    </button>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '12px' }}>
                  {GROUNDING_STEPS.map((step) => {
                    const done = groundingCompleted.includes(step.count);
                    return (
                      <div
                        key={step.count}
                        onClick={() =>
                          setGroundingCompleted((prev) =>
                            prev.includes(step.count) ? prev.filter((c) => c !== step.count) : [...prev, step.count]
                          )
                        }
                        style={{
                          padding: '16px',
                          borderRadius: '14px',
                          background: done ? '#d1fae5' : '#ffffff',
                          border: done ? '2px solid #059669' : '1.5px solid #e2e8f0',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <span
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '10px',
                              background: done ? '#059669' : '#f1f5f9',
                              color: done ? '#ffffff' : '#0f172a',
                              fontWeight: 800,
                              fontSize: '15px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {done ? '✓' : step.count}
                          </span>
                          <span style={{ fontSize: '22px' }}>{step.emoji}</span>
                        </div>
                        <div style={{ fontWeight: 800, fontSize: '14px', color: '#0f172a', marginBottom: '4px' }}>
                          {step.count} {step.title}
                        </div>
                        <div style={{ fontSize: '12px', color: '#475569', lineHeight: 1.5 }}>{step.prompt}</div>
                      </div>
                    );
                  })}
                </div>

                {groundingCompleted.length === GROUNDING_STEPS.length && (
                  <div
                    style={{
                      marginTop: '16px',
                      padding: '14px 18px',
                      borderRadius: '12px',
                      background: '#059669',
                      color: '#ffffff',
                      fontWeight: 700,
                      fontSize: '14px',
                      textAlign: 'center',
                    }}
                  >
                    🎉 Wonderful job! Take one slow deep breath—you are grounded and present right here, right now.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 3 Main Tool Cards */}
          <div className="row" style={{ marginBottom: '36px' }}>
            {tools.map((tool) => {
              const isSelected = selectedToolId === tool.id;

              return (
                <div key={tool.id} className="col-lg-4 col-md-6" style={{ marginBottom: '24px' }}>
                  <div
                    className="sh-tool-card sh-resource-card"
                    onClick={() => handleToolClick(tool.id)}
                    style={{
                      background: '#ffffff',
                      borderRadius: '24px',
                      cursor: 'pointer',
                      border: isSelected ? `2.5px solid ${tool.accent}` : '1.5px solid #e2e8f0',
                      boxShadow: isSelected
                        ? `0 20px 44px ${tool.accent}2b`
                        : '0 8px 24px rgba(15, 23, 42, 0.05)',
                      textAlign: 'left',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      height: '100%',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <div>
                      {/* Visual Cover Banner on Top of Each Main Tool Card */}
                      <div style={{ position: 'relative', height: '148px', overflow: 'hidden' }}>
                        <img
                          className="sh-audio-card-cover"
                          src={tool.coverImage}
                          alt={tool.name}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = tool.coverFallback;
                          }}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                        <div style={{ position: 'absolute', inset: 0, background: tool.coverOverlay }} />

                        <div
                          style={{
                            position: 'absolute',
                            top: '14px',
                            left: '16px',
                            right: '16px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <div
                            style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '14px',
                              background: 'rgba(255, 255, 255, 0.22)',
                              backdropFilter: 'blur(8px)',
                              border: '1px solid rgba(255, 255, 255, 0.35)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '25px',
                            }}
                          >
                            {tool.icon}
                          </div>

                          <span
                            style={{
                              backgroundColor: 'rgba(15, 23, 42, 0.68)',
                              backdropFilter: 'blur(6px)',
                              color: '#ffffff',
                              padding: '5px 13px',
                              borderRadius: '999px',
                              fontSize: '12px',
                              fontWeight: 700,
                              border: '1px solid rgba(255, 255, 255, 0.22)',
                            }}
                          >
                            {tool.count}
                          </span>
                        </div>

                        <div
                          style={{
                            position: 'absolute',
                            bottom: '12px',
                            left: '18px',
                            right: '18px',
                          }}
                        >
                          <div
                            style={{
                              fontSize: '11px',
                              fontWeight: 800,
                              textTransform: 'uppercase',
                              letterSpacing: '0.06em',
                              color: '#e2e8f0',
                            }}
                          >
                            {tool.badge}
                          </div>
                          <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: '#ffffff', margin: '2px 0 0 0' }}>
                            {tool.name}
                          </h3>
                        </div>
                      </div>

                      <div style={{ padding: '20px 24px 12px 24px' }}>
                        <p style={{ color: '#64748b', fontSize: '0.94rem', lineHeight: '1.6', margin: 0 }}>
                          {tool.description}
                        </p>
                      </div>
                    </div>

                    <div style={{ padding: '10px 24px 24px 24px' }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToolClick(tool.id);
                        }}
                        style={{
                          width: '100%',
                          padding: '12px 20px',
                          borderRadius: '12px',
                          border: isSelected ? 'none' : `1.5px solid ${tool.accent}`,
                          background: isSelected ? tool.gradient : '#ffffff',
                          color: isSelected ? '#ffffff' : tool.accent,
                          fontWeight: 700,
                          fontSize: '14px',
                          cursor: 'pointer',
                          boxShadow: isSelected ? `0 6px 18px ${tool.accent}33` : 'none',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                        }}
                      >
                        <span>{isSelected ? `✓ Viewing ${tool.name}` : tool.btnText}</span>
                        <span>{isSelected ? '↓' : '→'}</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* SEARCH & FILTER BAR ACROSS SELF-HELP RESOURCES */}
          <div
            id="self-help-detail-section"
            style={{
              background: '#ffffff',
              padding: '16px 22px',
              borderRadius: '18px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 20px rgba(15, 23, 42, 0.04)',
              marginBottom: '24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '14px',
            }}
          >
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {tools.map((t) => {
                const active = selectedToolId === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      setSelectedToolId(t.id);
                      setSelectedCategory(null);
                    }}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '10px',
                      border: active ? 'none' : '1px solid #e2e8f0',
                      background: active ? t.gradient : '#f8fafc',
                      color: active ? '#ffffff' : '#475569',
                      fontWeight: 700,
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    {t.icon} {t.name}
                  </button>
                );
              })}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: '1', maxWidth: '460px', minWidth: '260px' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="🔍 Search audios, categories, podcasts, worksheets..."
                  style={{
                    width: '100%',
                    padding: '9px 34px 9px 14px',
                    borderRadius: '10px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '13.5px',
                    outline: 'none',
                  }}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'transparent',
                      color: '#64748b',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 700,
                    }}
                  >
                    ×
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                title="View bookmarked resources"
                style={{
                  padding: '9px 14px',
                  borderRadius: '10px',
                  border: showFavoritesOnly ? '1.5px solid #f59e0b' : '1.5px solid #cbd5e1',
                  background: showFavoritesOnly ? '#fef3c7' : '#ffffff',
                  color: showFavoritesOnly ? '#b45309' : '#475569',
                  fontWeight: 700,
                  fontSize: '13px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {showFavoritesOnly ? '★ Saved' : '☆ Saved'} ({favorites.length})
              </button>
            </div>
          </div>

          {/* DYNAMIC DETAILS CONTAINER */}
          <div>
            {/* SECTION 1: AUDIO RESOURCES */}
            {selectedToolId === 1 && (
              <div
                style={{
                  background: '#ffffff',
                  borderRadius: '24px',
                  boxShadow: '0 14px 44px rgba(15, 23, 42, 0.08)',
                  border: '1px solid #e2e8f0',
                  overflow: 'hidden',
                }}
              >
                {/* Atmospheric Sanctuary Header Banner */}
                <div
                  style={{
                    position: 'relative',
                    padding: '32px 30px',
                    backgroundImage:
                      'linear-gradient(115deg, rgba(30, 27, 75, 0.92) 0%, rgba(76, 29, 149, 0.84) 55%, rgba(14, 116, 144, 0.78) 100%), url(/assets/images/auth-soothing-wellness.jpg)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    color: '#ffffff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '16px',
                  }}
                >
                  <div style={{ maxWidth: '620px' }}>
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        background: 'rgba(255, 255, 255, 0.15)',
                        backdropFilter: 'blur(8px)',
                        padding: '5px 14px',
                        borderRadius: '999px',
                        fontSize: '12px',
                        fontWeight: 700,
                        color: '#e9d5ff',
                        marginBottom: '10px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      <span>🎧 Immersive Soundscapes & Voice Guides</span>
                    </div>
                    <h3
                      style={{
                        fontSize: '1.6rem',
                        fontWeight: 800,
                        color: '#ffffff',
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                      }}
                    >
                      🎵 Guided Audio Sanctuary
                    </h3>
                    <p style={{ color: '#e2e8f0', fontSize: '14px', margin: '6px 0 0 0', lineHeight: 1.55 }}>
                      {selectedCategory
                        ? `Exploring curated audio sessions inside "${selectedCategory.name}"`
                        : 'Choose a visual sanctuary below to stream calming guided meditations, sleep journeys, and grounding exercises.'}
                    </p>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    {!selectedCategory && (
                      <div
                        style={{
                          display: 'inline-flex',
                          background: 'rgba(15, 23, 42, 0.45)',
                          backdropFilter: 'blur(10px)',
                          padding: '4px',
                          borderRadius: '12px',
                          border: '1px solid rgba(255, 255, 255, 0.18)',
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => setAudioViewMode('grid')}
                          style={{
                            padding: '7px 14px',
                            borderRadius: '9px',
                            border: 'none',
                            background: audioViewMode === 'grid' ? '#ffffff' : 'transparent',
                            color: audioViewMode === 'grid' ? '#1e1b4b' : '#e2e8f0',
                            fontSize: '12.5px',
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          🖼️ Visual Cards
                        </button>
                        <button
                          type="button"
                          onClick={() => setAudioViewMode('folders')}
                          style={{
                            padding: '7px 14px',
                            borderRadius: '9px',
                            border: 'none',
                            background: audioViewMode === 'folders' ? '#ffffff' : 'transparent',
                            color: audioViewMode === 'folders' ? '#1e1b4b' : '#e2e8f0',
                            fontSize: '12.5px',
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          📁 Folder List
                        </button>
                        <button
                          type="button"
                          onClick={() => setAudioViewMode('all-tracks')}
                          style={{
                            padding: '7px 14px',
                            borderRadius: '9px',
                            border: 'none',
                            background: audioViewMode === 'all-tracks' ? '#ffffff' : 'transparent',
                            color: audioViewMode === 'all-tracks' ? '#1e1b4b' : '#e2e8f0',
                            fontSize: '12.5px',
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          🎧 All Tracks ({audioResources.length})
                        </button>
                      </div>
                    )}

                    {selectedCategory && (
                      <button
                        type="button"
                        onClick={() => setSelectedCategory(null)}
                        style={{
                          background: 'rgba(255, 255, 255, 0.95)',
                          color: '#4c1d95',
                          border: 'none',
                          padding: '10px 18px',
                          borderRadius: '12px',
                          fontSize: '13px',
                          fontWeight: 800,
                          cursor: 'pointer',
                          boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)',
                        }}
                      >
                        ← Back to All Categories
                      </button>
                    )}
                  </div>
                </div>

                {selectedCategory || audioViewMode === 'all-tracks' ? (
                  <div style={{ padding: '28px' }}>
                    {selectedCategory && (() => {
                      const catVisual = getCategoryVisual(selectedCategory.name, 0);
                      return (
                        <div
                          style={{
                            position: 'relative',
                            borderRadius: '20px',
                            overflow: 'hidden',
                            marginBottom: '26px',
                            minHeight: '165px',
                            display: 'flex',
                            alignItems: 'flex-end',
                            padding: '24px 28px',
                            color: '#ffffff',
                            boxShadow: '0 10px 28px rgba(15, 23, 42, 0.12)',
                          }}
                        >
                          <img
                            src={
                              selectedCategory.imageUrl || selectedCategory.thumbnailUrl
                                ? getMediaUrl(selectedCategory.imageUrl || selectedCategory.thumbnailUrl)
                                : catVisual.image
                            }
                            alt={selectedCategory.name}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = catVisual.fallback;
                            }}
                            style={{
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              zIndex: 0,
                            }}
                          />
                          <div
                            style={{
                              position: 'absolute',
                              inset: 0,
                              background: catVisual.overlay,
                              zIndex: 1,
                            }}
                          />
                          <div
                            style={{
                              position: 'relative',
                              zIndex: 2,
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-end',
                              flexWrap: 'wrap',
                              gap: '16px',
                            }}
                          >
                            <div>
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  background: 'rgba(255,255,255,0.2)',
                                  backdropFilter: 'blur(8px)',
                                  padding: '4px 12px',
                                  borderRadius: '999px',
                                  fontSize: '12px',
                                  fontWeight: 700,
                                  marginBottom: '8px',
                                }}
                              >
                                {getCategoryIcon(selectedCategory.name)} {categoryAudios.length}{' '}
                                {categoryAudios.length === 1 ? 'Guided Session' : 'Guided Sessions'}
                              </span>
                              <h4 style={{ fontSize: '1.55rem', fontWeight: 800, color: '#ffffff', margin: '0 0 4px 0' }}>
                                {selectedCategory.name}
                              </h4>
                              <p style={{ fontSize: '13.5px', color: '#e2e8f0', margin: 0, maxWidth: '560px' }}>
                                {selectedCategory.description || catVisual.tagline}
                              </p>
                            </div>

                            {categoryAudios.length > 0 && (categoryAudios[0].audioUrl || categoryAudios[0].mediaUrl) && (
                              <button
                                type="button"
                                onClick={() => handlePlayTrack(categoryAudios[0])}
                                style={{
                                  padding: '10px 20px',
                                  borderRadius: '12px',
                                  border: 'none',
                                  background: '#ffffff',
                                  color: '#1e1b4b',
                                  fontWeight: 800,
                                  fontSize: '13px',
                                  cursor: 'pointer',
                                  boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
                                }}
                              >
                                ▶ Play First Session
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {(selectedCategory ? categoryAudios : audioResources).length === 0 ? (
                      <div
                        style={{
                          padding: '48px 20px',
                          textAlign: 'center',
                          background: '#f8fafc',
                          borderRadius: '16px',
                          border: '1.5px dashed #cbd5e1',
                          color: '#64748b',
                        }}
                      >
                        <div style={{ fontSize: '36px', marginBottom: '10px' }}>🎧</div>
                        <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '16px' }}>
                          {selectedCategory
                            ? `No audio tracks found in "${selectedCategory.name}".`
                            : 'No matching audio tracks found.'}
                        </div>
                        <div style={{ fontSize: '13px', marginTop: '4px' }}>
                          Try clearing your search filter or exploring another category folder.
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                          gap: '22px',
                        }}
                      >
                        {(selectedCategory ? categoryAudios : audioResources).map((audio, idx) => {
                          const id = audio._id || audio.id;
                          const audioSrc = audio.audioUrl || audio.mediaUrl;
                          const isCurrentTrack = nowPlaying && (nowPlaying._id || nowPlaying.id) === id;
                          const isFav = favorites.includes(id);
                          const visual = getCategoryVisual(audio.category || audio.title, idx);
                          const coverImg = audio.imageUrl || audio.thumbnailUrl ? getMediaUrl(audio.imageUrl || audio.thumbnailUrl) : visual.image;

                          return (
                            <div
                              key={id}
                              className="sh-resource-card"
                              style={{
                                background: isCurrentTrack ? '#faf5ff' : '#ffffff',
                                border: isCurrentTrack ? '2px solid #7c3aed' : '1.5px solid #e2e8f0',
                                borderRadius: '20px',
                                overflow: 'hidden',
                                boxShadow: '0 6px 20px rgba(15, 23, 42, 0.05)',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                              }}
                            >
                              <div>
                                {/* Visual Cover Art Header */}
                                <div
                                  onClick={() => audioSrc && handlePlayTrack(audio)}
                                  style={{
                                    position: 'relative',
                                    height: '155px',
                                    overflow: 'hidden',
                                    cursor: audioSrc ? 'pointer' : 'default',
                                  }}
                                >
                                  <img
                                    className="sh-audio-card-cover"
                                    src={coverImg}
                                    alt={audio.title}
                                    onError={(e) => {
                                      e.currentTarget.onerror = null;
                                      e.currentTarget.src = visual.fallback;
                                    }}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      display: 'block',
                                    }}
                                  />
                                  <div
                                    style={{
                                      position: 'absolute',
                                      inset: 0,
                                      background: visual.overlay,
                                    }}
                                  />

                                  {/* Top Badges on Artwork */}
                                  <div
                                    style={{
                                      position: 'absolute',
                                      top: '12px',
                                      left: '12px',
                                      right: '12px',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <span
                                      style={{
                                        background: 'rgba(15, 23, 42, 0.65)',
                                        backdropFilter: 'blur(6px)',
                                        color: '#ffffff',
                                        fontSize: '11.5px',
                                        fontWeight: 700,
                                        padding: '4px 11px',
                                        borderRadius: '999px',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                      }}
                                    >
                                      {getCategoryIcon(audio.category)} {audio.category || 'Audio Guide'}
                                    </span>

                                    <button
                                      type="button"
                                      onClick={(e) => toggleFavorite(id, e)}
                                      title={isFav ? 'Remove from Saved' : 'Save to Favorites'}
                                      style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        border: '1px solid rgba(255,255,255,0.25)',
                                        background: 'rgba(15, 23, 42, 0.55)',
                                        backdropFilter: 'blur(6px)',
                                        cursor: 'pointer',
                                        fontSize: '15px',
                                        color: isFav ? '#fbbf24' : '#ffffff',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      {isFav ? '★' : '☆'}
                                    </button>
                                  </div>

                                  {/* Center Floating Play Orb & Bottom Duration */}
                                  <div
                                    style={{
                                      position: 'absolute',
                                      bottom: '12px',
                                      left: '14px',
                                      right: '14px',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'flex-end',
                                    }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      {audioSrc && (
                                        <span
                                          style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '50%',
                                            background: isCurrentTrack && isPlaying ? '#7c3aed' : 'rgba(255, 255, 255, 0.92)',
                                            color: isCurrentTrack && isPlaying ? '#ffffff' : '#1e1b4b',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: 800,
                                            fontSize: '14px',
                                            boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
                                          }}
                                        >
                                          {isCurrentTrack && isPlaying ? '⏸' : '▶'}
                                        </span>
                                      )}
                                      {isCurrentTrack && isPlaying && (
                                        <div
                                          style={{
                                            display: 'inline-flex',
                                            alignItems: 'flex-end',
                                            gap: '3px',
                                            height: '18px',
                                            padding: '0 6px',
                                          }}
                                        >
                                          <span className="sh-eq-bar" style={{ animationDelay: '0s' }} />
                                          <span className="sh-eq-bar" style={{ animationDelay: '0.2s' }} />
                                          <span className="sh-eq-bar" style={{ animationDelay: '0.4s' }} />
                                        </div>
                                      )}
                                    </div>

                                    {(audio.duration || audio.readTime) && (
                                      <span
                                        style={{
                                          fontSize: '11.5px',
                                          color: '#ffffff',
                                          fontWeight: 700,
                                          background: 'rgba(15, 23, 42, 0.65)',
                                          padding: '3px 9px',
                                          borderRadius: '8px',
                                        }}
                                      >
                                        ⏱ {audio.duration || audio.readTime}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Card Body */}
                                <div style={{ padding: '18px 20px 10px 20px' }}>
                                  <h5
                                    style={{
                                      fontSize: '1.12rem',
                                      fontWeight: 800,
                                      color: '#0f172a',
                                      marginBottom: '6px',
                                    }}
                                  >
                                    {audio.title}
                                  </h5>

                                  {audio.summary && (
                                    <p
                                      style={{
                                        fontSize: '13px',
                                        color: '#64748b',
                                        lineHeight: '1.55',
                                        marginBottom: '10px',
                                      }}
                                    >
                                      {audio.summary}
                                    </p>
                                  )}

                                  {audio.narrator && (
                                    <div
                                      style={{
                                        fontSize: '12px',
                                        color: '#7c3aed',
                                        fontWeight: 600,
                                        marginBottom: '6px',
                                      }}
                                    >
                                      🎙️ Guided by {audio.narrator}
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div
                                style={{
                                  padding: '12px 20px 20px 20px',
                                  borderTop: '1px solid #f1f5f9',
                                }}
                              >
                                {audioSrc ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <button
                                      type="button"
                                      onClick={() => handlePlayTrack(audio)}
                                      style={{
                                        width: '100%',
                                        padding: '10px 14px',
                                        borderRadius: '10px',
                                        border: 'none',
                                        background:
                                          isCurrentTrack && isPlaying
                                            ? '#4f46e5'
                                            : 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                                        color: '#ffffff',
                                        fontWeight: 700,
                                        fontSize: '13px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                      }}
                                    >
                                      <span>
                                        {isCurrentTrack && isPlaying ? '⏸ Pause Session' : '▶ Play in Mini Player'}
                                      </span>
                                    </button>
                                    <audio
                                      controls
                                      src={getMediaUrl(audioSrc)}
                                      style={{ width: '100%', height: '36px', borderRadius: '8px' }}
                                    />
                                  </div>
                                ) : (
                                  <span style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
                                    Audio stream will be available soon
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : audioViewMode === 'grid' ? (
                  <div
                    style={{
                      padding: '28px',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(275px, 1fr))',
                      gap: '22px',
                    }}
                  >
                    {loading ? (
                      <div style={{ padding: '48px', textAlign: 'center', color: '#64748b', gridColumn: '1 / -1' }}>
                        Loading audio sanctuaries...
                      </div>
                    ) : filteredCategories.length === 0 ? (
                      <div style={{ padding: '48px', textAlign: 'center', color: '#64748b', gridColumn: '1 / -1' }}>
                        No categories match your filter.
                      </div>
                    ) : (
                      filteredCategories.map((cat, idx) => {
                        const count = audioResources.filter(
                          (a) => a.category?.trim().toLowerCase() === cat.name?.trim().toLowerCase()
                        ).length;
                        const visual = getCategoryVisual(cat.name, idx);

                        return (
                          <div
                            key={cat._id || cat.name}
                            className="sh-resource-card"
                            onClick={() => setSelectedCategory(cat)}
                            style={{
                              borderRadius: '20px',
                              background: '#ffffff',
                              border: '1.5px solid #e2e8f0',
                              cursor: 'pointer',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              overflow: 'hidden',
                              boxShadow: '0 6px 20px rgba(15, 23, 42, 0.05)',
                            }}
                          >
                            {/* Atmospheric Image Header */}
                            <div style={{ position: 'relative', height: '168px', overflow: 'hidden' }}>
                              <img
                                className="sh-audio-card-cover"
                                src={
                                  cat.imageUrl || cat.thumbnailUrl
                                    ? getMediaUrl(cat.imageUrl || cat.thumbnailUrl)
                                    : visual.image
                                }
                                alt={cat.name}
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = visual.fallback;
                                }}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  display: 'block',
                                }}
                              />
                              <div
                                style={{
                                  position: 'absolute',
                                  inset: 0,
                                  background: visual.overlay,
                                }}
                              />

                              {/* Floating Icon & Track Badge */}
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '14px',
                                  left: '14px',
                                  right: '14px',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                }}
                              >
                                <span
                                  style={{
                                    width: '42px',
                                    height: '42px',
                                    borderRadius: '12px',
                                    background: 'rgba(255, 255, 255, 0.22)',
                                    backdropFilter: 'blur(8px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '21px',
                                  }}
                                >
                                  {getCategoryIcon(cat.name)}
                                </span>

                                <span
                                  style={{
                                    background: count > 0 ? 'rgba(15, 23, 42, 0.72)' : 'rgba(15, 23, 42, 0.5)',
                                    backdropFilter: 'blur(6px)',
                                    color: '#ffffff',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    padding: '5px 12px',
                                    borderRadius: '999px',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                  }}
                                >
                                  🎵 {count} {count === 1 ? 'Track' : 'Tracks'}
                                </span>
                              </div>

                              {/* Title Overlay at Bottom of Image */}
                              <div
                                style={{
                                  position: 'absolute',
                                  bottom: '14px',
                                  left: '16px',
                                  right: '16px',
                                }}
                              >
                                <h5
                                  style={{
                                    fontSize: '1.15rem',
                                    fontWeight: 800,
                                    color: '#ffffff',
                                    margin: 0,
                                    textShadow: '0 2px 8px rgba(0,0,0,0.35)',
                                  }}
                                >
                                  {cat.name}
                                </h5>
                              </div>
                            </div>

                            {/* Card Description & CTA */}
                            <div
                              style={{
                                padding: '16px 18px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                flex: 1,
                                gap: '12px',
                              }}
                            >
                              <p
                                style={{
                                  fontSize: '13px',
                                  color: '#64748b',
                                  lineHeight: 1.5,
                                  margin: 0,
                                }}
                              >
                                {cat.description || visual.tagline}
                              </p>

                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  paddingTop: '10px',
                                  borderTop: '1px solid #f1f5f9',
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: '12.5px',
                                    color: visual.accent,
                                    fontWeight: 800,
                                  }}
                                >
                                  Explore Sanctuary
                                </span>
                                <span
                                  style={{
                                    width: '28px',
                                    height: '28px',
                                    borderRadius: '50%',
                                    background: visual.badgeBg,
                                    color: visual.accent,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 800,
                                    fontSize: '14px',
                                  }}
                                >
                                  →
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                ) : (
                  <div>
                    <div
                      style={{
                        padding: '14px 28px',
                        borderBottom: '1.5px solid #e2e8f0',
                        fontSize: '13px',
                        fontWeight: 800,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: '#64748b',
                        background: '#ffffff',
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>Category Sanctuary</span>
                      <span>Available Tracks</span>
                    </div>

                    {loading ? (
                      <div style={{ padding: '48px', textAlign: 'center', color: '#64748b' }}>
                        Loading audio categories...
                      </div>
                    ) : filteredCategories.length === 0 ? (
                      <div style={{ padding: '48px', textAlign: 'center', color: '#64748b' }}>
                        No categories match your filter.
                      </div>
                    ) : (
                      filteredCategories.map((cat, idx) => {
                        const isEven = idx % 2 === 1;
                        const rowBg = isEven ? '#f8fafc' : '#ffffff';
                        const count = audioResources.filter(
                          (a) => a.category?.trim().toLowerCase() === cat.name?.trim().toLowerCase()
                        ).length;
                        const visual = getCategoryVisual(cat.name, idx);

                        return (
                          <div
                            key={cat._id || cat.name}
                            className="sh-folder-row"
                            onClick={() => setSelectedCategory(cat)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '14px 28px',
                              background: rowBg,
                              borderBottom: '1px solid #f1f5f9',
                              cursor: 'pointer',
                              gap: '16px',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f5f3ff';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = rowBg;
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                              <div
                                style={{
                                  position: 'relative',
                                  width: '56px',
                                  height: '56px',
                                  borderRadius: '14px',
                                  overflow: 'hidden',
                                  flexShrink: 0,
                                  boxShadow: '0 4px 10px rgba(15, 23, 42, 0.1)',
                                }}
                              >
                                <img
                                  src={
                                    cat.imageUrl || cat.thumbnailUrl
                                      ? getMediaUrl(cat.imageUrl || cat.thumbnailUrl)
                                      : visual.image
                                  }
                                  alt={cat.name}
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = visual.fallback;
                                  }}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                                <div
                                  style={{
                                    position: 'absolute',
                                    inset: 0,
                                    background: 'rgba(15, 23, 42, 0.25)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '20px',
                                  }}
                                >
                                  {getCategoryIcon(cat.name)}
                                </div>
                              </div>

                              <div>
                                <div
                                  style={{
                                    fontSize: '15.5px',
                                    fontWeight: 800,
                                    color: '#1e293b',
                                  }}
                                >
                                  {cat.name}
                                </div>
                                <div style={{ fontSize: '12.5px', color: '#64748b', marginTop: '2px' }}>
                                  {cat.description || visual.tagline}
                                </div>
                              </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                              <span
                                style={{
                                  background: count > 0 ? '#ede9fe' : '#f1f5f9',
                                  color: count > 0 ? '#6d28d9' : '#64748b',
                                  fontSize: '12px',
                                  fontWeight: 700,
                                  padding: '5px 12px',
                                  borderRadius: '999px',
                                }}
                              >
                                {count} {count === 1 ? 'audio' : 'audios'}
                              </span>
                              <span style={{ color: '#7c3aed', fontSize: '18px', fontWeight: 700 }}>›</span>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            )}

            {/* SECTION 2: PODCASTS & EDUCATIONAL PDF LIBRARY */}
            {selectedToolId === 2 && (() => {
              const podcastTopics = [
                'all',
                ...Array.from(new Set(podcastResources.map((p) => (p.category || 'Podcast').trim()).filter(Boolean))),
              ];
              const displayedPodcasts = podcastResources.filter((p) => {
                if (selectedPodcastTopic !== 'all' && (p.category || 'Podcast').trim() !== selectedPodcastTopic) {
                  return false;
                }
                if (podcastPdfOnly && !p.pdfUrl) {
                  return false;
                }
                return true;
              });
              const featuredPodcast = displayedPodcasts[0] || null;

              return (
                <div
                  style={{
                    background: '#ffffff',
                    borderRadius: '24px',
                    boxShadow: '0 14px 44px rgba(15, 23, 42, 0.08)',
                    border: '1px solid #bae6fd',
                    overflow: 'hidden',
                  }}
                >
                  {/* Atmospheric Podcast Studio Header Banner */}
                  <div
                    style={{
                      position: 'relative',
                      padding: '32px 30px',
                      backgroundImage:
                        'linear-gradient(115deg, rgba(12, 74, 110, 0.94) 0%, rgba(3, 105, 161, 0.86) 55%, rgba(30, 58, 138, 0.82) 100%), url(/assets/images/blog/blog-1-1.jpg)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      color: '#ffffff',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '16px',
                    }}
                  >
                    <div style={{ maxWidth: '640px' }}>
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          background: 'rgba(255, 255, 255, 0.16)',
                          backdropFilter: 'blur(8px)',
                          padding: '5px 14px',
                          borderRadius: '999px',
                          fontSize: '12px',
                          fontWeight: 700,
                          color: '#bae6fd',
                          marginBottom: '10px',
                          border: '1px solid rgba(255, 255, 255, 0.22)',
                        }}
                      >
                        🎙️ Therapist Conversations & Companion Study Guides
                      </div>
                      <h3
                        style={{
                          fontSize: '1.6rem',
                          fontWeight: 800,
                          color: '#ffffff',
                          margin: 0,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                        }}
                      >
                        🎧 Podcasts & Educational PDF Library
                      </h3>
                      <p style={{ color: '#e0f2fe', fontSize: '14px', margin: '6px 0 0 0', lineHeight: 1.55 }}>
                        Listen to expert clinical discussions and download companion PDF episode guides, reflection sheets, and transcripts.
                      </p>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={() => setPodcastPdfOnly(!podcastPdfOnly)}
                        style={{
                          padding: '9px 16px',
                          borderRadius: '12px',
                          border: podcastPdfOnly ? 'none' : '1px solid rgba(255,255,255,0.3)',
                          background: podcastPdfOnly ? '#ffffff' : 'rgba(15, 23, 42, 0.45)',
                          color: podcastPdfOnly ? '#0369a1' : '#ffffff',
                          fontWeight: 800,
                          fontSize: '12.5px',
                          cursor: 'pointer',
                          backdropFilter: 'blur(8px)',
                        }}
                      >
                        📄 {podcastPdfOnly ? '✓ Showing Episodes with PDF' : 'Filter: With PDF Guide'}
                      </button>
                    </div>
                  </div>

                  {/* Interactive Topic Filter Bar */}
                  {podcastTopics.length > 1 && (
                    <div
                      style={{
                        padding: '14px 28px',
                        background: '#f8fafc',
                        borderBottom: '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flexWrap: 'wrap',
                      }}
                    >
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#64748b', marginRight: '4px' }}>
                        TOPICS:
                      </span>
                      {podcastTopics.map((topic) => {
                        const active = selectedPodcastTopic === topic;
                        return (
                          <button
                            key={topic}
                            type="button"
                            onClick={() => setSelectedPodcastTopic(topic)}
                            style={{
                              padding: '6px 14px',
                              borderRadius: '999px',
                              border: active ? 'none' : '1px solid #cbd5e1',
                              background: active ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' : '#ffffff',
                              color: active ? '#ffffff' : '#475569',
                              fontSize: '12.5px',
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            {topic === 'all' ? `All Episodes (${podcastResources.length})` : topic}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <div style={{ padding: '28px' }}>
                    {/* Featured Episode Spotlight Banner */}
                    {featuredPodcast && displayedPodcasts.length > 1 && (() => {
                      const featVisual = getPodcastVisual(featuredPodcast, 0);
                      const featCover =
                        featuredPodcast.imageUrl || featuredPodcast.thumbnailUrl
                          ? getMediaUrl(featuredPodcast.imageUrl || featuredPodcast.thumbnailUrl)
                          : featVisual.image;
                      const featAudio = featuredPodcast.audioUrl || featuredPodcast.mediaUrl;
                      const featId = featuredPodcast._id || featuredPodcast.id;
                      const isFeatPlaying = nowPlaying && (nowPlaying._id || nowPlaying.id) === featId && isPlaying;

                      return (
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                            borderRadius: '20px',
                            overflow: 'hidden',
                            border: '1.5px solid #bae6fd',
                            background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                            marginBottom: '28px',
                            boxShadow: '0 8px 26px rgba(2, 132, 199, 0.08)',
                          }}
                        >
                          <div style={{ position: 'relative', minHeight: '210px', overflow: 'hidden' }}>
                            <img
                              src={featCover}
                              alt={featuredPodcast.title}
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = featVisual.fallback;
                              }}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                            <div style={{ position: 'absolute', inset: 0, background: featVisual.overlay }} />
                            <span
                              style={{
                                position: 'absolute',
                                top: '14px',
                                left: '14px',
                                background: '#0284c7',
                                color: '#ffffff',
                                padding: '5px 12px',
                                borderRadius: '999px',
                                fontSize: '11.5px',
                                fontWeight: 800,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                              }}
                            >
                              ⭐ Featured Episode Spotlight
                            </span>
                          </div>

                          <div
                            style={{
                              padding: '24px 26px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                            }}
                          >
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                                <span
                                  style={{
                                    background: '#e0f2fe',
                                    color: '#0369a1',
                                    fontSize: '11.5px',
                                    fontWeight: 700,
                                    padding: '4px 10px',
                                    borderRadius: '999px',
                                  }}
                                >
                                  🎧 {featuredPodcast.category || 'Clinical Podcast'}
                                </span>
                                {(featuredPodcast.duration || featuredPodcast.readTime) && (
                                  <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 700 }}>
                                    ⏱ {featuredPodcast.duration || featuredPodcast.readTime}
                                  </span>
                                )}
                              </div>

                              <h4 style={{ fontSize: '1.35rem', fontWeight: 800, color: '#0f172a', margin: '0 0 8px 0' }}>
                                {featuredPodcast.title}
                              </h4>

                              {featuredPodcast.narrator && (
                                <div style={{ fontSize: '13px', color: '#0284c7', fontWeight: 700, marginBottom: '8px' }}>
                                  🎙️ Hosted by {featuredPodcast.narrator}
                                </div>
                              )}

                              {featuredPodcast.summary && (
                                <p style={{ fontSize: '13.5px', color: '#475569', lineHeight: 1.6, margin: '0 0 16px 0' }}>
                                  {featuredPodcast.summary}
                                </p>
                              )}
                            </div>

                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                              {featAudio && (
                                <button
                                  type="button"
                                  onClick={() => handlePlayTrack(featuredPodcast)}
                                  style={{
                                    padding: '10px 18px',
                                    borderRadius: '10px',
                                    border: 'none',
                                    background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                                    color: '#ffffff',
                                    fontWeight: 800,
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 14px rgba(2, 132, 199, 0.25)',
                                  }}
                                >
                                  {isFeatPlaying ? '⏸ Pause Episode' : '▶ Listen to Featured Episode'}
                                </button>
                              )}

                              <button
                                type="button"
                                onClick={() => {
                                  setReflectionNote('');
                                  setPreviewResource(featuredPodcast);
                                }}
                                style={{
                                  padding: '10px 16px',
                                  borderRadius: '10px',
                                  border: '1.5px solid #7dd3fc',
                                  background: '#ffffff',
                                  color: '#0369a1',
                                  fontWeight: 700,
                                  fontSize: '13px',
                                  cursor: 'pointer',
                                }}
                              >
                                👁️ Episode Notes
                              </button>

                              {featuredPodcast.pdfUrl && (
                                <a
                                  href={getMediaUrl(featuredPodcast.pdfUrl)}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{
                                    padding: '10px 16px',
                                    borderRadius: '10px',
                                    border: '1.5px solid #0284c7',
                                    background: '#f0f9ff',
                                    color: '#0369a1',
                                    fontWeight: 700,
                                    fontSize: '13px',
                                    textDecoration: 'none',
                                  }}
                                >
                                  📄 Companion PDF
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {displayedPodcasts.length === 0 ? (
                      <div
                        style={{
                          padding: '48px',
                          textAlign: 'center',
                          background: '#f0f9ff',
                          borderRadius: '16px',
                          border: '1.5px dashed #7dd3fc',
                          color: '#64748b',
                        }}
                      >
                        <div style={{ fontSize: '36px', marginBottom: '10px' }}>🎙️</div>
                        <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '16px' }}>
                          No podcast episodes found matching your filter.
                        </div>
                        <div style={{ fontSize: '13px', marginTop: '4px' }}>
                          Try selecting "All Episodes" or clearing your search query.
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                          gap: '22px',
                        }}
                      >
                        {displayedPodcasts.map((item, idx) => {
                          const id = item._id || item.id;
                          const audioSrc = item.audioUrl || item.mediaUrl;
                          const isFav = favorites.includes(id);
                          const isCurrentTrack = nowPlaying && (nowPlaying._id || nowPlaying.id) === id;
                          const visual = getPodcastVisual(item, idx);
                          const coverImg =
                            item.imageUrl || item.thumbnailUrl
                              ? getMediaUrl(item.imageUrl || item.thumbnailUrl)
                              : visual.image;

                          return (
                            <div
                              key={id}
                              className="sh-resource-card"
                              style={{
                                background: isCurrentTrack ? '#f0f9ff' : '#ffffff',
                                border: isCurrentTrack ? '2px solid #0284c7' : '1.5px solid #e0f2fe',
                                borderRadius: '20px',
                                overflow: 'hidden',
                                boxShadow: '0 6px 20px rgba(15, 23, 42, 0.05)',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                              }}
                            >
                              <div>
                                {/* Visual Cover Art Header */}
                                <div
                                  onClick={() =>
                                    audioSrc
                                      ? handlePlayTrack(item)
                                      : (setReflectionNote(''), setPreviewResource(item))
                                  }
                                  style={{
                                    position: 'relative',
                                    height: '162px',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                  }}
                                >
                                  <img
                                    className="sh-audio-card-cover"
                                    src={coverImg}
                                    alt={item.title}
                                    onError={(e) => {
                                      e.currentTarget.onerror = null;
                                      e.currentTarget.src = visual.fallback;
                                    }}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                  />
                                  <div style={{ position: 'absolute', inset: 0, background: visual.overlay }} />

                                  {/* Top Category & Favorite Star */}
                                  <div
                                    style={{
                                      position: 'absolute',
                                      top: '12px',
                                      left: '12px',
                                      right: '12px',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <span
                                      style={{
                                        background: 'rgba(15, 23, 42, 0.68)',
                                        backdropFilter: 'blur(6px)',
                                        color: '#ffffff',
                                        fontSize: '11.5px',
                                        fontWeight: 700,
                                        padding: '4px 11px',
                                        borderRadius: '999px',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                      }}
                                    >
                                      🎧 {item.category || 'Podcast'}
                                    </span>

                                    <button
                                      type="button"
                                      onClick={(e) => toggleFavorite(id, e)}
                                      style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        border: '1px solid rgba(255,255,255,0.25)',
                                        background: 'rgba(15, 23, 42, 0.55)',
                                        backdropFilter: 'blur(6px)',
                                        cursor: 'pointer',
                                        fontSize: '15px',
                                        color: isFav ? '#fbbf24' : '#ffffff',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      {isFav ? '★' : '☆'}
                                    </button>
                                  </div>

                                  {/* Bottom Play Orb / PDF Badge & Duration */}
                                  <div
                                    style={{
                                      position: 'absolute',
                                      bottom: '12px',
                                      left: '14px',
                                      right: '14px',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'flex-end',
                                    }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      {audioSrc ? (
                                        <span
                                          style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '50%',
                                            background: isCurrentTrack && isPlaying ? '#0284c7' : 'rgba(255,255,255,0.94)',
                                            color: isCurrentTrack && isPlaying ? '#ffffff' : '#0369a1',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: 800,
                                            fontSize: '14px',
                                            boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
                                          }}
                                        >
                                          {isCurrentTrack && isPlaying ? '⏸' : '▶'}
                                        </span>
                                      ) : (
                                        <span
                                          style={{
                                            background: 'rgba(2, 132, 199, 0.88)',
                                            color: '#ffffff',
                                            fontSize: '11px',
                                            fontWeight: 700,
                                            padding: '4px 10px',
                                            borderRadius: '8px',
                                          }}
                                        >
                                          {visual.badge}
                                        </span>
                                      )}
                                      {isCurrentTrack && isPlaying && (
                                        <div
                                          style={{
                                            display: 'inline-flex',
                                            alignItems: 'flex-end',
                                            gap: '3px',
                                            height: '18px',
                                            padding: '0 6px',
                                          }}
                                        >
                                          <span className="sh-eq-bar" style={{ background: '#38bdf8', animationDelay: '0s' }} />
                                          <span className="sh-eq-bar" style={{ background: '#38bdf8', animationDelay: '0.2s' }} />
                                          <span className="sh-eq-bar" style={{ background: '#38bdf8', animationDelay: '0.4s' }} />
                                        </div>
                                      )}
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      {item.pdfUrl && (
                                        <span
                                          style={{
                                            fontSize: '11px',
                                            color: '#ffffff',
                                            fontWeight: 700,
                                            background: 'rgba(2, 132, 199, 0.85)',
                                            padding: '3px 8px',
                                            borderRadius: '8px',
                                          }}
                                        >
                                          📄 PDF
                                        </span>
                                      )}
                                      {(item.duration || item.readTime) && (
                                        <span
                                          style={{
                                            fontSize: '11.5px',
                                            color: '#ffffff',
                                            fontWeight: 700,
                                            background: 'rgba(15, 23, 42, 0.68)',
                                            padding: '3px 9px',
                                            borderRadius: '8px',
                                          }}
                                        >
                                          ⏱ {item.duration || item.readTime}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Card Body */}
                                <div style={{ padding: '20px 22px 10px 22px' }}>
                                  <h5
                                    style={{
                                      fontSize: '1.16rem',
                                      fontWeight: 800,
                                      color: '#0f172a',
                                      marginBottom: '6px',
                                    }}
                                  >
                                    {item.title}
                                  </h5>

                                  {item.narrator && (
                                    <div
                                      style={{
                                        fontSize: '12.5px',
                                        color: '#0284c7',
                                        fontWeight: 700,
                                        marginBottom: '8px',
                                      }}
                                    >
                                      🎙️ Host / Guest: {item.narrator}
                                    </div>
                                  )}

                                  {item.summary && (
                                    <p
                                      style={{
                                        fontSize: '13.5px',
                                        color: '#475569',
                                        lineHeight: '1.6',
                                        marginBottom: '12px',
                                      }}
                                    >
                                      {item.summary}
                                    </p>
                                  )}

                                  {audioSrc && (
                                    <div style={{ marginBottom: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                      <button
                                        type="button"
                                        onClick={() => handlePlayTrack(item)}
                                        style={{
                                          padding: '9px 14px',
                                          borderRadius: '10px',
                                          border: '1px solid #bae6fd',
                                          background: isCurrentTrack && isPlaying ? '#0284c7' : '#f0f9ff',
                                          color: isCurrentTrack && isPlaying ? '#ffffff' : '#0369a1',
                                          fontWeight: 700,
                                          fontSize: '12.5px',
                                          cursor: 'pointer',
                                        }}
                                      >
                                        {isCurrentTrack && isPlaying ? '⏸ Pause Episode' : '▶ Listen in Sticky Player'}
                                      </button>
                                      <audio controls src={getMediaUrl(audioSrc)} style={{ width: '100%', height: '36px' }} />
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div
                                style={{
                                  padding: '12px 22px 22px 22px',
                                  borderTop: '1px solid #f1f5f9',
                                  display: 'flex',
                                  gap: '10px',
                                }}
                              >
                                <button
                                  type="button"
                                  onClick={() => {
                                    setReflectionNote('');
                                    setPreviewResource(item);
                                  }}
                                  style={{
                                    padding: '10px 14px',
                                    borderRadius: '10px',
                                    border: '1.5px solid #bae6fd',
                                    background: '#f0f9ff',
                                    color: '#0369a1',
                                    fontWeight: 700,
                                    fontSize: '12.5px',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  👁️ Notes
                                </button>

                                {item.pdfUrl ? (
                                  <a
                                    href={getMediaUrl(item.pdfUrl)}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{
                                      flex: 1,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      gap: '8px',
                                      background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                                      color: '#ffffff',
                                      padding: '10px 16px',
                                      borderRadius: '10px',
                                      fontWeight: 700,
                                      fontSize: '13px',
                                      textDecoration: 'none',
                                      boxShadow: '0 4px 12px rgba(2, 132, 199, 0.25)',
                                    }}
                                  >
                                    📄 Download PDF ({item.pdfFileName || 'Guide'})
                                  </a>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setReflectionNote('');
                                      setPreviewResource(item);
                                    }}
                                    style={{
                                      flex: 1,
                                      padding: '10px 16px',
                                      borderRadius: '10px',
                                      border: 'none',
                                      background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                                      color: '#ffffff',
                                      fontWeight: 700,
                                      fontSize: '13px',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    📖 Read Episode Summary & Notes
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* SECTION 3: ACTIVITIES & WORKSHEETS */}
            {selectedToolId === 3 && (() => {
              const worksheetTags = [
                'all',
                ...Array.from(
                  new Set(
                    activityResources
                      .flatMap((w) => [(w.category || '').trim(), (w.targetAudience || '').trim()])
                      .filter(Boolean)
                  )
                ),
              ];
              const displayedWorksheets = activityResources.filter((w) => {
                if (selectedWorksheetFilter === 'all') return true;
                return (
                  (w.category || '').trim() === selectedWorksheetFilter ||
                  (w.targetAudience || '').trim() === selectedWorksheetFilter
                );
              });

              return (
                <div
                  style={{
                    background: '#ffffff',
                    borderRadius: '24px',
                    boxShadow: '0 14px 44px rgba(15, 23, 42, 0.08)',
                    border: '1px solid #bbf7d0',
                    overflow: 'hidden',
                  }}
                >
                  {/* Atmospheric Mindful Journaling & CBT Header Banner */}
                  <div
                    style={{
                      position: 'relative',
                      padding: '32px 30px',
                      backgroundImage:
                        'linear-gradient(115deg, rgba(6, 78, 59, 0.94) 0%, rgba(5, 150, 105, 0.86) 55%, rgba(13, 148, 136, 0.82) 100%), url(/assets/images/blog/blog-1-5.jpg)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      color: '#ffffff',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '16px',
                    }}
                  >
                    <div style={{ maxWidth: '650px' }}>
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          background: 'rgba(255, 255, 255, 0.16)',
                          backdropFilter: 'blur(8px)',
                          padding: '5px 14px',
                          borderRadius: '999px',
                          fontSize: '12px',
                          fontWeight: 700,
                          color: '#a7f3d0',
                          marginBottom: '10px',
                          border: '1px solid rgba(255, 255, 255, 0.22)',
                        }}
                      >
                        ✍️ Evidence-Based CBT & Self-Reflection Exercises
                      </div>
                      <h3
                        style={{
                          fontSize: '1.6rem',
                          fontWeight: 800,
                          color: '#ffffff',
                          margin: 0,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                        }}
                      >
                        📝 Activities & Printable Worksheets
                      </h3>
                      <p style={{ color: '#d1fae5', fontSize: '14px', margin: '6px 0 0 0', lineHeight: 1.55 }}>
                        Practice structured CBT thought records, gratitude prompts, and emotional regulation worksheets online or download printable PDFs.
                      </p>
                    </div>

                    <div
                      style={{
                        background: 'rgba(15, 23, 42, 0.42)',
                        backdropFilter: 'blur(8px)',
                        padding: '10px 18px',
                        borderRadius: '14px',
                        border: '1px solid rgba(255,255,255,0.2)',
                        fontSize: '13px',
                        fontWeight: 700,
                        color: '#ecfdf5',
                      }}
                    >
                      ✨ Tip: Click <strong>"👁️ Interactive Guide"</strong> on any card to practice right in your browser!
                    </div>
                  </div>

                  {/* Interactive Worksheet Category / Audience Filter Pills */}
                  {worksheetTags.length > 1 && (
                    <div
                      style={{
                        padding: '14px 28px',
                        background: '#f8fafc',
                        borderBottom: '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flexWrap: 'wrap',
                      }}
                    >
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#64748b', marginRight: '4px' }}>
                        FILTER BY:
                      </span>
                      {worksheetTags.map((tag) => {
                        const active = selectedWorksheetFilter === tag;
                        return (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => setSelectedWorksheetFilter(tag)}
                            style={{
                              padding: '6px 14px',
                              borderRadius: '999px',
                              border: active ? 'none' : '1px solid #cbd5e1',
                              background: active ? 'linear-gradient(135deg, #059669 0%, #047857 100%)' : '#ffffff',
                              color: active ? '#ffffff' : '#475569',
                              fontSize: '12.5px',
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            {tag === 'all' ? `All Worksheets (${activityResources.length})` : tag}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <div style={{ padding: '28px' }}>
                    {displayedWorksheets.length === 0 ? (
                      <div
                        style={{
                          padding: '48px',
                          textAlign: 'center',
                          background: '#f0fdf4',
                          borderRadius: '16px',
                          border: '1.5px dashed #86efac',
                          color: '#64748b',
                        }}
                      >
                        <div style={{ fontSize: '36px', marginBottom: '10px' }}>📝</div>
                        <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '16px' }}>
                          No worksheets found matching your filter.
                        </div>
                        <div style={{ fontSize: '13px', marginTop: '4px' }}>
                          Try selecting "All Worksheets" or clearing your search query.
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                          gap: '22px',
                        }}
                      >
                        {displayedWorksheets.map((item, idx) => {
                          const id = item._id || item.id;
                          const worksheetUrl = item.worksheetFileUrl || item.mediaUrl;
                          const isFav = favorites.includes(id);
                          const visual = getWorksheetVisual(item, idx);
                          const coverImg =
                            item.imageUrl || item.thumbnailUrl
                              ? getMediaUrl(item.imageUrl || item.thumbnailUrl)
                              : visual.image;

                          return (
                            <div
                              key={id}
                              className="sh-resource-card"
                              style={{
                                background: '#ffffff',
                                border: '1.5px solid #dcfce7',
                                borderRadius: '20px',
                                overflow: 'hidden',
                                boxShadow: '0 6px 20px rgba(15, 23, 42, 0.05)',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                              }}
                            >
                              <div>
                                {/* Visual Cover Art Header */}
                                <div
                                  onClick={() => {
                                    setReflectionNote('');
                                    setPreviewResource(item);
                                  }}
                                  style={{
                                    position: 'relative',
                                    height: '162px',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                  }}
                                >
                                  <img
                                    className="sh-audio-card-cover"
                                    src={coverImg}
                                    alt={item.title}
                                    onError={(e) => {
                                      e.currentTarget.onerror = null;
                                      e.currentTarget.src = visual.fallback;
                                    }}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                  />
                                  <div style={{ position: 'absolute', inset: 0, background: visual.overlay }} />

                                  {/* Top Category & Favorite Star */}
                                  <div
                                    style={{
                                      position: 'absolute',
                                      top: '12px',
                                      left: '12px',
                                      right: '12px',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <span
                                      style={{
                                        background: 'rgba(15, 23, 42, 0.68)',
                                        backdropFilter: 'blur(6px)',
                                        color: '#ffffff',
                                        fontSize: '11.5px',
                                        fontWeight: 700,
                                        padding: '4px 11px',
                                        borderRadius: '999px',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                      }}
                                    >
                                      📝 {item.category || 'Worksheet'}
                                    </span>

                                    <button
                                      type="button"
                                      onClick={(e) => toggleFavorite(id, e)}
                                      style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        border: '1px solid rgba(255,255,255,0.25)',
                                        background: 'rgba(15, 23, 42, 0.55)',
                                        backdropFilter: 'blur(6px)',
                                        cursor: 'pointer',
                                        fontSize: '15px',
                                        color: isFav ? '#fbbf24' : '#ffffff',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      {isFav ? '★' : '☆'}
                                    </button>
                                  </div>

                                  {/* Bottom Exercise Tag & Target Audience */}
                                  <div
                                    style={{
                                      position: 'absolute',
                                      bottom: '12px',
                                      left: '14px',
                                      right: '14px',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <span
                                      style={{
                                        background: 'rgba(5, 150, 105, 0.9)',
                                        color: '#ffffff',
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        padding: '4px 10px',
                                        borderRadius: '8px',
                                      }}
                                    >
                                      {visual.tag}
                                    </span>

                                    {item.targetAudience && (
                                      <span
                                        style={{
                                          fontSize: '11px',
                                          color: '#ffffff',
                                          fontWeight: 700,
                                          background: 'rgba(15, 23, 42, 0.68)',
                                          padding: '4px 10px',
                                          borderRadius: '8px',
                                        }}
                                      >
                                        👥 {item.targetAudience}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Card Body */}
                                <div style={{ padding: '20px 22px 10px 22px' }}>
                                  <h5
                                    style={{
                                      fontSize: '1.16rem',
                                      fontWeight: 800,
                                      color: '#0f172a',
                                      marginBottom: '8px',
                                    }}
                                  >
                                    {item.title}
                                  </h5>

                                  {item.summary && (
                                    <p
                                      style={{
                                        fontSize: '13.5px',
                                        color: '#475569',
                                        lineHeight: '1.6',
                                        marginBottom: '12px',
                                      }}
                                    >
                                      {item.summary}
                                    </p>
                                  )}

                                  {item.instructions && (
                                    <div
                                      style={{
                                        background: '#f0fdf4',
                                        padding: '12px 14px',
                                        borderRadius: '12px',
                                        fontSize: '12.5px',
                                        color: '#166534',
                                        marginBottom: '8px',
                                        borderLeft: '3.5px solid #10b981',
                                        lineHeight: 1.5,
                                      }}
                                    >
                                      <strong>How to practice:</strong> {item.instructions}
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div
                                style={{
                                  padding: '12px 22px 22px 22px',
                                  borderTop: '1px solid #f1f5f9',
                                  display: 'flex',
                                  gap: '10px',
                                }}
                              >
                                <button
                                  type="button"
                                  onClick={() => {
                                    setReflectionNote('');
                                    setPreviewResource(item);
                                  }}
                                  style={{
                                    padding: '11px 14px',
                                    borderRadius: '10px',
                                    border: '1.5px solid #a7f3d0',
                                    background: '#ecfdf5',
                                    color: '#047857',
                                    fontWeight: 700,
                                    fontSize: '12.5px',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  👁️ Interactive Guide
                                </button>

                                {worksheetUrl ? (
                                  <a
                                    href={getMediaUrl(worksheetUrl)}
                                    target="_blank"
                                    download
                                    rel="noreferrer"
                                    style={{
                                      flex: 1,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      gap: '8px',
                                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                                      color: '#ffffff',
                                      padding: '11px 16px',
                                      borderRadius: '10px',
                                      fontWeight: 700,
                                      fontSize: '13px',
                                      textDecoration: 'none',
                                      boxShadow: '0 4px 12px rgba(5, 150, 105, 0.25)',
                                    }}
                                  >
                                    📥 Download ({item.worksheetFileName || 'PDF'})
                                  </a>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setReflectionNote('');
                                      setPreviewResource(item);
                                    }}
                                    style={{
                                      flex: 1,
                                      padding: '11px 16px',
                                      borderRadius: '10px',
                                      border: 'none',
                                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                                      color: '#ffffff',
                                      fontWeight: 700,
                                      fontSize: '13px',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    ✍️ Open Reflection Exercise
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Tips Section */}
          <div className="row" style={{ marginTop: '56px' }}>
            <div className="col-lg-12">
              <div
                className="tips-section wow fadeInUp"
                style={{
                  background: 'rgba(255,255,255,0.95)',
                  backdropFilter: 'blur(8px)',
                  padding: '38px',
                  borderRadius: '24px',
                  boxShadow: '0 10px 32px rgba(80,80,120,0.07)',
                  border: '1px solid #e2e8f0',
                }}
              >
                <h3 style={{ marginBottom: '24px', color: '#1e1b4b', fontWeight: 800, fontSize: '1.4rem' }}>
                  💡 How to Get the Most Out of Your Self-Help Sanctuary
                </h3>
                <div className="row">
                  <div className="col-md-6" style={{ marginBottom: '18px' }}>
                    <div style={{ padding: '18px', borderRadius: '16px', background: '#f8fafc', height: '100%' }}>
                      <h4 style={{ fontWeight: 800, color: '#6d28d9', fontSize: '15.5px', marginBottom: '6px' }}>
                        ✓ Practice Micro-Moments Consistently
                      </h4>
                      <p style={{ fontSize: '13.5px', color: '#475569', margin: 0, lineHeight: 1.6 }}>
                        Even 5 to 10 minutes of guided audio grounding or completing a short worksheet reflection daily
                        strengthens emotional resilience over time.
                      </p>
                    </div>
                  </div>
                  <div className="col-md-6" style={{ marginBottom: '18px' }}>
                    <div style={{ padding: '18px', borderRadius: '16px', background: '#f8fafc', height: '100%' }}>
                      <h4 style={{ fontWeight: 800, color: '#0284c7', fontSize: '15.5px', marginBottom: '6px' }}>
                        ✓ Bookmark Your Personal Toolkit
                      </h4>
                      <p style={{ fontSize: '13.5px', color: '#475569', margin: 0, lineHeight: 1.6 }}>
                        Click the star (☆) on any audio session, podcast episode, or worksheet to save your go-to
                        calming resources for instant access whenever you need them.
                      </p>
                    </div>
                  </div>
                  <div className="col-md-6" style={{ marginBottom: '18px' }}>
                    <div style={{ padding: '18px', borderRadius: '16px', background: '#f8fafc', height: '100%' }}>
                      <h4 style={{ fontWeight: 800, color: '#059669', fontSize: '15.5px', marginBottom: '6px' }}>
                        ✓ Share Reflections in Therapy
                      </h4>
                      <p style={{ fontSize: '13.5px', color: '#475569', margin: 0, lineHeight: 1.6 }}>
                        Print or save your completed worksheets and discuss your insights with your licensed therapist
                        during your next consultation.
                      </p>
                    </div>
                  </div>
                  <div className="col-md-6" style={{ marginBottom: '18px' }}>
                    <div style={{ padding: '18px', borderRadius: '16px', background: '#f8fafc', height: '100%' }}>
                      <h4 style={{ fontWeight: 800, color: '#b45309', fontSize: '15.5px', marginBottom: '6px' }}>
                        ✓ Professional Care When Needed
                      </h4>
                      <p style={{ fontSize: '13.5px', color: '#475569', margin: 0, lineHeight: 1.6 }}>
                        Self-help tools are a supportive companion to—not a substitute for—personalized clinical care
                        from a qualified mental health professional.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WORKSHEET / PODCAST INTERACTIVE GUIDE & REFLECTION MODAL */}
      {previewResource && (() => {
        const isPodcast = previewResource.section === 'podcast' || previewResource.type === 'guide' || Boolean(previewResource.pdfUrl && previewResource.section !== 'activity');
        const modalVisual = isPodcast ? getPodcastVisual(previewResource, 0) : getWorksheetVisual(previewResource, 0);
        const modalCover =
          previewResource.imageUrl || previewResource.thumbnailUrl
            ? getMediaUrl(previewResource.imageUrl || previewResource.thumbnailUrl)
            : modalVisual.image;
        const downloadUrl = previewResource.worksheetFileUrl || previewResource.pdfUrl || previewResource.mediaUrl;

        return (
          <div
            onClick={() => setPreviewResource(null)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(6px)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#ffffff',
                borderRadius: '24px',
                maxWidth: '640px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 24px 56px rgba(0,0,0,0.28)',
                overflow: 'hidden',
              }}
            >
              {/* Modal Cover Banner */}
              <div style={{ position: 'relative', height: '165px', overflow: 'hidden' }}>
                <img
                  src={modalCover}
                  alt={previewResource.title}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = modalVisual.fallback;
                  }}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                <div style={{ position: 'absolute', inset: 0, background: modalVisual.overlay }} />
                <button
                  type="button"
                  onClick={() => setPreviewResource(null)}
                  style={{
                    position: 'absolute',
                    top: '14px',
                    right: '14px',
                    border: 'none',
                    background: 'rgba(15, 23, 42, 0.65)',
                    color: '#ffffff',
                    width: '34px',
                    height: '34px',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    fontWeight: 800,
                  }}
                >
                  ✕
                </button>
                <div style={{ position: 'absolute', bottom: '14px', left: '22px', right: '22px' }}>
                  <span
                    style={{
                      background: isPodcast ? '#0284c7' : '#059669',
                      color: '#ffffff',
                      fontSize: '11.5px',
                      fontWeight: 700,
                      padding: '4px 12px',
                      borderRadius: '999px',
                    }}
                  >
                    {isPodcast ? '🎧 Podcast Guide & Notes' : `📝 ${previewResource.category || 'Interactive Worksheet'}`}
                  </span>
                  <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: '#ffffff', margin: '8px 0 0 0' }}>
                    {previewResource.title}
                  </h3>
                </div>
              </div>

              <div style={{ padding: '26px' }}>
                {previewResource.summary && (
                  <p style={{ fontSize: '14px', color: '#475569', lineHeight: 1.65, marginBottom: '16px' }}>
                    {previewResource.summary}
                  </p>
                )}

                {previewResource.content && previewResource.content !== previewResource.summary && (
                  <div
                    style={{
                      background: '#f8fafc',
                      padding: '16px',
                      borderRadius: '12px',
                      fontSize: '13.5px',
                      color: '#334155',
                      lineHeight: 1.65,
                      marginBottom: '16px',
                    }}
                  >
                    {previewResource.content}
                  </div>
                )}

                {previewResource.instructions && (
                  <div
                    style={{
                      background: '#ecfdf5',
                      borderLeft: '4px solid #10b981',
                      padding: '14px 16px',
                      borderRadius: '10px',
                      fontSize: '13.5px',
                      color: '#065f46',
                      marginBottom: '18px',
                    }}
                  >
                    <strong>Step-by-Step Instructions:</strong>
                    <div style={{ marginTop: '4px', lineHeight: 1.6 }}>{previewResource.instructions}</div>
                  </div>
                )}

                {/* Interactive Self-Reflection Scratchpad */}
                <div
                  style={{
                    background: '#f8fafc',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '14px',
                    padding: '16px',
                    marginBottom: '20px',
                  }}
                >
                  <label
                    style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 800,
                      color: '#1e293b',
                      marginBottom: '6px',
                    }}
                  >
                    ✍️ Quick Personal Reflection Scratchpad (Private to your browser session)
                  </label>
                  <textarea
                    rows={3}
                    value={reflectionNote}
                    onChange={(e) => setReflectionNote(e.target.value)}
                    placeholder="Jot down your thoughts, triggers, or key takeaways from this exercise right here..."
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '10px',
                      border: '1px solid #cbd5e1',
                      fontSize: '13px',
                      fontFamily: 'inherit',
                      outline: 'none',
                    }}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setPreviewResource(null)}
                    style={{
                      padding: '10px 18px',
                      borderRadius: '10px',
                      border: '1px solid #cbd5e1',
                      background: '#ffffff',
                      color: '#475569',
                      fontWeight: 700,
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    Close
                  </button>
                  {downloadUrl && (
                    <a
                      href={getMediaUrl(downloadUrl)}
                      target="_blank"
                      download
                      rel="noreferrer"
                      style={{
                        padding: '10px 20px',
                        borderRadius: '10px',
                        background: isPodcast ? '#0284c7' : '#059669',
                        color: '#ffffff',
                        fontWeight: 700,
                        fontSize: '13px',
                        textDecoration: 'none',
                      }}
                    >
                      📥 Download {isPodcast ? 'Episode PDF' : 'Printable Worksheet'}
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* STICKY FLOATING MINI AUDIO PLAYER */}
      {nowPlaying && (
        <div
          style={{
            position: 'fixed',
            bottom: '18px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'calc(100% - 32px)',
            maxWidth: '880px',
            background: 'rgba(15, 23, 42, 0.96)',
            backdropFilter: 'blur(14px)',
            color: '#ffffff',
            borderRadius: '18px',
            padding: '14px 22px',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.35)',
            border: '1px solid rgba(167, 139, 250, 0.3)',
            zIndex: 9990,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '14px',
          }}
        >
          <audio
            ref={stickyAudioRef}
            src={nowPlaying.resolvedUrl}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
            onTimeUpdate={(e) =>
              setAudioProgress({
                currentTime: e.currentTarget.currentTime,
                duration: e.currentTarget.duration || 0,
              })
            }
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: '200px', flex: 1 }}>
            <button
              type="button"
              onClick={() => handlePlayTrack(nowPlaying)}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                border: 'none',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                color: '#ffffff',
                fontSize: '16px',
                cursor: 'pointer',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isPlaying ? '⏸' : '▶'}
            </button>
            <div style={{ overflow: 'hidden' }}>
              <div
                style={{
                  fontWeight: 800,
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {nowPlaying.title}
              </div>
              <div style={{ fontSize: '12px', color: '#a5b4fc' }}>
                {nowPlaying.category || 'Guided Session'}
                {nowPlaying.narrator ? ` • ${nowPlaying.narrator}` : ''}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1.5, minWidth: '220px' }}>
            <span style={{ fontSize: '11.5px', color: '#cbd5e1', minWidth: '34px' }}>
              {formatTime(audioProgress.currentTime)}
            </span>
            <input
              type="range"
              min={0}
              max={audioProgress.duration || 100}
              value={audioProgress.currentTime}
              onChange={(e) => {
                const val = Number(e.target.value);
                if (stickyAudioRef.current) {
                  stickyAudioRef.current.currentTime = val;
                }
                setAudioProgress((prev) => ({ ...prev, currentTime: val }));
              }}
              style={{ flex: 1, accentColor: '#8b5cf6', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '11.5px', color: '#cbd5e1', minWidth: '34px' }}>
              {formatTime(audioProgress.duration)}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              onClick={handleCyclePlaybackRate}
              title="Playback Speed"
              style={{
                padding: '5px 10px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.08)',
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {playbackRate}x
            </button>
            <button
              type="button"
              onClick={() => {
                if (stickyAudioRef.current) stickyAudioRef.current.pause();
                setNowPlaying(null);
                setIsPlaying(false);
              }}
              title="Close Player"
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                border: 'none',
                background: 'rgba(255,255,255,0.12)',
                color: '#ffffff',
                cursor: 'pointer',
                fontWeight: 700,
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default SelfHelpPage;
